/*
 * ActivityMonitor.jsx — SeniorLife x PacSana Fall & Activity Monitoring
 * =======================================================================
 * Production React port of the standalone demo (index.html). Every visual
 * component is lifted VERBATIM from the demo, so UI/UX is identical. The only
 * change is the data seam: instead of the demo's inlined MOCK_DATA/PATIENTS
 * blocks, this module fetches a normalized payload for ONE subject from the
 * Mongo-backed backend API (see backend/pacsana_contract.js).
 *
 * Subject key: a unique `seniorlife_id` OR `external_id`. The PacSana bracelet
 * is an attribute of that subject that may or may not be bound yet:
 *   - sensor bound   -> full monitor screen (identical to demo)
 *   - sensor unbound -> SensorSetupEmptyState ("add a sensor")
 *
 * Contract compliance (see logic JSON `frontendConstraints`): this component
 * does NO raw PacSana parsing, NO abnormality/trend computation, and NEVER
 * binds sensor IDs to residents itself. All derived fields are computed by the
 * backend (see backend/pacsana_contract.js) and delivered ready-to-render.
 *
 * Exports:
 *   default  ActivityMonitorScreen  — container: fetch-by-id + render
 *   named    ActivityMonitor        — presentational, data-agnostic component
 *   named    useActivityData        — fetch hook (GET /monitoring/subject/:id)
 *   named    _toActivityData        — resident bundle -> component data shape
 *
 * NOTE: component tree uses React.createElement (no JSX syntax). `react` is the
 * only runtime dep; the logic JSON is bundled as design-time config. The trend
 * chart is a hand-rolled SVG, so Recharts is intentionally not imported.
 */
/* eslint-disable */
import React, { useState, useMemo, useEffect } from "react";
import LOGIC_DATA from "./pacsanaLogic.json";
import { formatDayLabel } from "../../../../utlis/activityTrendDate";
import { getCurrentLocale } from "../../../../utlis/locale";
import {
  formatActivityClock,
  formatActivityWeekPosition,
  localizeActivityText,
} from "../activityMonitoringI18n";

function _typeof(o) {
  "@babel/helpers - typeof";
  return (
    (_typeof =
      "function" == typeof Symbol && "symbol" == typeof Symbol.iterator
        ? function (o) {
            return typeof o;
          }
        : function (o) {
            return o &&
              "function" == typeof Symbol &&
              o.constructor === Symbol &&
              o !== Symbol.prototype
              ? "symbol"
              : typeof o;
          }),
    _typeof(o)
  );
}
function _slicedToArray(r, e) {
  return (
    _arrayWithHoles(r) ||
    _iterableToArrayLimit(r, e) ||
    _unsupportedIterableToArray(r, e) ||
    _nonIterableRest()
  );
}
function _nonIterableRest() {
  throw new TypeError(
    "Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.",
  );
}
function _unsupportedIterableToArray(r, a) {
  if (r) {
    if ("string" == typeof r) return _arrayLikeToArray(r, a);
    var t = {}.toString.call(r).slice(8, -1);
    return (
      "Object" === t && r.constructor && (t = r.constructor.name),
      "Map" === t || "Set" === t
        ? Array.from(r)
        : "Arguments" === t ||
            /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t)
          ? _arrayLikeToArray(r, a)
          : void 0
    );
  }
}
function _arrayLikeToArray(r, a) {
  (null == a || a > r.length) && (a = r.length);
  for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e];
  return n;
}
function _iterableToArrayLimit(r, l) {
  var t =
    null == r
      ? null
      : ("undefined" != typeof Symbol && r[Symbol.iterator]) || r["@@iterator"];
  if (null != t) {
    var e,
      n,
      i,
      u,
      a = [],
      f = !0,
      o = !1;
    try {
      if (((i = (t = t.call(r)).next), 0 === l)) {
        if (Object(t) !== t) return;
        f = !1;
      } else
        for (
          ;
          !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l);
          f = !0
        );
    } catch (r) {
      (o = !0), (n = r);
    } finally {
      try {
        if (!f && null != t["return"] && ((u = t["return"]()), Object(u) !== u))
          return;
      } finally {
        if (o) throw n;
      }
    }
    return a;
  }
}
function _arrayWithHoles(r) {
  if (Array.isArray(r)) return r;
}
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r &&
      (o = o.filter(function (r) {
        return Object.getOwnPropertyDescriptor(e, r).enumerable;
      })),
      t.push.apply(t, o);
  }
  return t;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2
      ? ownKeys(Object(t), !0).forEach(function (r) {
          _defineProperty(e, r, t[r]);
        })
      : Object.getOwnPropertyDescriptors
        ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t))
        : ownKeys(Object(t)).forEach(function (r) {
            Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
          });
  }
  return e;
}
function _defineProperty(e, r, t) {
  return (
    (r = _toPropertyKey(r)) in e
      ? Object.defineProperty(e, r, {
          value: t,
          enumerable: !0,
          configurable: !0,
          writable: !0,
        })
      : (e[r] = t),
    e
  );
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == _typeof(i) ? i : i + "";
}
function _toPrimitive(t, r) {
  if ("object" != _typeof(t) || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != _typeof(i)) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
/* React hooks come from the ES import above. Recharts is unused (SVG chart). */

/* ============================================================
   Design tokens + helpers
   ============================================================ */
/* ============================================================
   ActivityMonitor — Stages 1-3 (mock-only, prop-ready, integration-documented)

   Stage history:
   - Stage 1: self-contained, mock-only, prop-ready component (ACCEPTED).
   - Stage 2: Sensor Details modal/panel + zoneLabels / hardwareErrorLabels (ACCEPTED).
   - Stage 3: API-readiness — JSDoc prop typedefs + integration TODOs (CURRENT).
              No live wiring; no fetch; no SSE/WebSocket; no MQTT; no visual change.

   Constraints honored:
   - No backend calls, no SSE/WebSocket, no direct PacSana API calls.
   - No raw PacSana vocabulary in UI (no possible_fall, no raw bracelet events).
   - No hard-coded product logic in JSX — all labels/colors/copy/order
     live in defaultActivityMonitorLogic (mirror of Activity_Monitor_Logic.JSON).
   - Mock data shape mirrors normalized backend contract.
   - Layout standard: Dynamic Action Plan (DM Sans, C palette, card patterns).
   - Activity Trend = stacked column chart (visual treatment from Mobility HTML).
   ============================================================

   INTEGRATION CONTRACT — Stage 3
   ----------------------------------------------------------------
   Architecture:
       PacSana Cloud/Webhook  →  SeniorLife backend  →  React app
                                   (this component)

   The frontend MUST NOT (re-affirmed for Stage 3):
   - Connect to PacSana directly.
   - Hold or transmit PacSana credentials, API keys, or device tokens.
   - Parse raw PacSana bracelet events (e.g. possible_fall, activity payloads).
   - Compute the fall lifecycle.
   - Deduplicate alert events.
   - Bind PacSana device IDs to residents.
   - Compute abnormalities.
   - Compute trend buckets.

   The SeniorLife backend OWNS (re-affirmed for Stage 3):
   - Fall lifecycle state machine (Suspected → Detected → Recovered).
   - Alert deduplication.
   - PacSana device-ID ↔ resident-ID binding.
   - Abnormality detection (Gait Decline, Unusual Night Activity, etc.).
   - Trend bucketing (active/sedentary hours per interval).
   - Device health normalization (ONLINE / NOT_WORN / OFFLINE / UNKNOWN).
   - Battery, gateway, and data quality status reporting.

   The frontend ONLY:
   - Renders normalized data passed via `activityMonitorData`.
   - Renders labels/colors/copy/order from `activityMonitorLogic`
     (Activity_Monitor_Logic.JSON).
   - Falls back to mock data + mock logic when props are absent
     (dev/QA mode only — never in production).
   ============================================================ */

/* ---------- Design tokens (ported from Dynamic Action Plan) ---------- */
var C = {
  navy: "#1E2F52",
  teal: "#2E8FA0",
  tealMuted: "#7FCAD4",
  salmon: "#C96B64",
  bg: "#f1f5f9",
  dark: "#141E30",
  body: "#344860",
  faint: "#6A8090",
  border: "#C0D8E0",
  borderLight: "#D8EBF0",
  amber: "#D97706",
  amberBg: "#FEF3E2",
  amberBorder: "#FCD34D",
  amberText: "#92400E",
  red: "#B91C1C",
  redBg: "#FEE2E2",
  redBorder: "#FCA5A5",
  redText: "#7F1D1D",
  green: "#065F46",
  greenBg: "#D1FAE5",
  greenBorder: "#6EE7B7",
  greenV: "#15803D",
  greenVBg: "#DCFCE7",
  blue: "#1155cc",
  blueBg: "#EFF6FF",
  blueBorder: "#BFDBFE",
  gridline: "#E2E8F0",
  tabularNums: "tabular-nums",
  // Chart-specific — distinct from semantic alert colors
  chartCouch: "#EAB308",
  // ochre golden yellow
  chartVacant: "#94A3B8",
  // slate — neutral filler
  chartToilet: "#2563EB", // sharp blue
};
var FONT_STACK = "var(--font-family-primary)";

/* ---------- Color token → palette lookup ---------- */
var COLOR_TOKEN = {
  red: {
    value: C.red,
    text: C.redText,
    bg: C.redBg,
    border: C.redBorder,
  },
  amber: {
    value: C.amber,
    text: C.amberText,
    bg: C.amberBg,
    border: C.amberBorder,
  },
  green: {
    value: C.greenV,
    text: C.green,
    bg: C.greenBg,
    border: C.greenBorder,
  },
  teal: {
    value: C.teal,
    text: C.teal,
    bg: "#E8F6F9",
    border: C.tealMuted,
  },
  blue: {
    value: C.blue,
    text: C.blue,
    bg: C.blueBg,
    border: C.blueBorder,
  },
  gray: {
    value: C.faint,
    text: C.faint,
    bg: "#F1F5F9",
    border: C.border,
  },
  navy: {
    value: C.navy,
    text: C.navy,
    bg: "#E5EAF2",
    border: C.border,
  },
  // Chart-specific tokens — do not use for alerts/status
  chart_couch: {
    value: C.chartCouch,
    text: C.chartCouch,
    bg: "#FAF0EE",
    border: "#E8B4AE",
  },
  chart_vacant: {
    value: C.chartVacant,
    text: "#475569",
    bg: "#F1F5F9",
    border: "#CBD5E1",
  },
  chart_toilet: {
    value: C.chartToilet,
    text: "#1E4F8C",
    bg: "#EFF6FF",
    border: "#93C5FD",
  },
};
var tokenColor = function tokenColor(t) {
  return COLOR_TOKEN[t] || COLOR_TOKEN.gray;
};

/* ============================================================
   defaultActivityMonitorLogic
   Mirror of Activity_Monitor_Logic.JSON (Stage 0B).
   Frontend consumes labels, colors, copy, display order — nothing else.
   ============================================================ */

/**
 * @typedef {Object} StatusCodeEntry
 * @property {string} internal           Backend status enum (e.g. "IN_BED").
 * @property {string} label              Staff-facing label.
 * @property {string} color              Semantic color token.
 * @property {string} icon               Icon key resolved by <StatusIcon>.
 * @property {string} [renderIf]         Gate flag (e.g. "backend_emits_toilet_status").
 */

/**
 * @typedef {Object} AlertStateEntry
 * @property {("FALL_DETECTED"|"FALL_SUSPECTED"|"FALL_RECOVERED"|"NONE")} id
 * @property {string} label
 * @property {string} color
 * @property {number} priority
 */

/**
 * @typedef {Object} AbnormalityDefinition
 * @property {("NIGHT_WANDERING"|"SLOWED_MOBILITY"|"EXTENDED_TOILET_STAY"|"NONE")} id
 * @property {string} label
 * @property {string} icon
 * @property {(string|null)} definition  Staff-readable explanation.
 */

/**
 * @typedef {Object} DeviceHealthEntry
 * @property {("ONLINE"|"OFFLINE"|"BAD_ANGLE"|"UNKNOWN")} state
 * @property {string} label
 * @property {string} color
 * @property {number} precedence
 * @property {string} icon
 */

/**
 * @typedef {Object} TrendSeriesEntry
 * @property {("bed"|"couch"|"toilet"|"vacant")} id
 * @property {string} label
 * @property {string} color
 * @property {string} [renderIf]   e.g. "backend_emits_toilet" — gates the toilet series.
 */

/**
 * @typedef {Object} ZoneLabelEntry
 * @property {("BED"|"COUCH"|"TOILET")} type
 * @property {string} label
 * @property {string} [renderIf]
 */

/**
 * @typedef {Object} HardwareErrorLabelEntry
 * @property {string} code        Normalized SeniorLife error code (NOT raw radar.error).
 * @property {string} label       Staff-facing label.
 */

/**
 * @typedef {Object} ActivityMonitorLogic
 * @property {string} version
 * @property {string} font
 * @property {StatusCodeEntry[]}        statusCodes
 * @property {AlertStateEntry[]}        alertStates
 * @property {AbnormalityDefinition[]}  abnormalityDefinitions
 * @property {DeviceHealthEntry[]}      deviceHealthLabels
 * @property {ZoneLabelEntry[]}         zoneLabels
 * @property {HardwareErrorLabelEntry[]} hardwareErrorLabels
 * @property {{ chartType:string, intervalDefault:string, yAxisMaxHours:number, series:TrendSeriesEntry[] }} activityTrend
 * @property {Object} thresholds         Backend-owned thresholds. Frontend may display, never enforce.
 * @property {Object.<string,string>} copy  All user-facing strings.
 *
 * Source of truth: Activity_Monitor_Logic.XLSX → Activity_Monitor_Logic.JSON.
 * The constant below is a build-time mirror used only as a mock fallback.
 */

/* ============================================================
   Logic accessors + all UI components
   ============================================================ */
var _findBy = function _findBy(arr, key, val) {
  return (arr || []).find(function (o) {
    return o[key] === val;
  });
};
var getStatusEntry = function getStatusEntry(logic, internal) {
  return (
    _findBy(logic.statusCodes, "internal", internal) ||
    _findBy(logic.statusCodes, "internal", "VACANT")
  );
};
var getAlertEntry = function getAlertEntry(logic, id) {
  return (
    _findBy(logic.alertStates, "id", id || "NONE") ||
    _findBy(logic.alertStates, "id", "NONE")
  );
};
var getAbnormalityEntry = function getAbnormalityEntry(logic, id) {
  return _findBy(logic.abnormalityDefinitions, "id", id || "NONE");
};
var getDeviceHealth = function getDeviceHealth(logic, state) {
  return (
    _findBy(logic.deviceHealthLabels, "state", state) ||
    _findBy(logic.deviceHealthLabels, "state", "UNKNOWN")
  );
};
var t = function t(logic, key) {
  return (logic.copy && logic.copy[key]) || key;
};

// Stage 2 — resolve backend zone object → staff-readable label.
// Priority: backend-supplied zone.label → logic.zoneLabels lookup → raw type → '—'.
var resolveZoneLabel = function resolveZoneLabel(logic, zone) {
  if (!zone) return "—";
  if (zone.label) return zone.label;
  var entry = _findBy(logic.zoneLabels, "type", zone.type);
  return (entry && entry.label) || zone.type || "—";
};

// Stage 2 — resolve backend hardware error → staff-readable label.
// Priority: backend-supplied error.label → logic.hardwareErrorLabels lookup → generic copy.
// We deliberately do NOT surface raw error.code values in user-facing UI.
var resolveErrorLabel = function resolveErrorLabel(logic, error) {
  if (!error) return t(logic, "hardwareErrorGeneric");
  if (error.label) return error.label;
  var entry = _findBy(logic.hardwareErrorLabels, "code", error.code);
  return (entry && entry.label) || t(logic, "hardwareErrorGeneric");
};

// Stage 2 — tiny template helper for copy strings with {placeholder} tokens.
var _interpolate = function _interpolate(tmpl, vars) {
  return String(tmpl).replace(/\{(\w+)\}/g, function (_, k) {
    return vars[k] != null ? String(vars[k]) : "";
  });
};
var getAbnormalityPatternCopy = function getAbnormalityPatternCopy(
  logic,
  signal,
) {
  return _findBy(logic.abnormalityPatternCopy, "signal", signal);
};
var getTrendDirection = function getTrendDirection(logic, direction) {
  return (
    _findBy(logic.abnormalityTrendDirection, "direction", direction) ||
    _findBy(logic.abnormalityTrendDirection, "direction", "STABLE")
  );
};
var getInsightCopy = function getInsightCopy(logic, signal) {
  return (
    _findBy(logic.activityInsightCopy, "signal", signal || "NONE") ||
    _findBy(logic.activityInsightCopy, "signal", "NONE")
  );
};
var interpolate = function interpolate(tmpl, vars) {
  return String(tmpl).replace(/\{(\w+)\}/g, function (_, k) {
    return vars[k] != null ? String(vars[k]) : "";
  });
};
var visibleTrendSeries = function visibleTrendSeries(logic, buckets) {
  return (logic.activityTrend.series || [])
    .filter(function (s) {
      if (!s.renderIf) return true;
      if (s.renderIf === "backend_emits_toilet") {
        return (buckets || []).some(function (b) {
          return typeof b[s.id] === "number";
        });
      }
      return true;
    })
    .sort(function (a, b) {
      return (a.displayOrder || 0) - (b.displayOrder || 0);
    });
};

/* ---------- Time helpers ---------- */
var _pad = function _pad(n) {
  return String(n).padStart(2, "0");
};
var formatFullStamp = function formatFullStamp(iso) {
  if (!iso) return "";
  var d = new Date(iso);
  if (getCurrentLocale() === "ja") {
    return d.toLocaleString("ja-JP", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  var month = d.toLocaleDateString("en-US", {
    month: "short",
  });
  var hour12 = ((d.getHours() + 11) % 12) + 1;
  var ampm = d.getHours() >= 12 ? "PM" : "AM";
  return ""
    .concat(month, " ")
    .concat(d.getDate(), ", ")
    .concat(d.getFullYear(), " at ")
    .concat(hour12, ":")
    .concat(_pad(d.getMinutes()), " ")
    .concat(ampm);
};
var formatShortDate = function formatShortDate(iso) {
  return formatDayLabel(iso);
};
var formatRelativeTime = function formatRelativeTime(iso) {
  var staleThresholdMins =
    arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 60;
  if (!iso) return null;
  var diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)
    return {
      text: "".concat(diff, "s ago"),
      stale: false,
    };
  if (diff < 3600)
    return {
      text: "".concat(Math.floor(diff / 60), " min ago"),
      stale: false,
    };
  if (diff < staleThresholdMins * 60)
    return {
      text: "".concat(Math.floor(diff / 60), " min ago"),
      stale: false,
    };
  var d = new Date(iso);
  var h = ((d.getHours() + 11) % 12) + 1;
  var ap = d.getHours() >= 12 ? "PM" : "AM";
  return {
    text: "Today ".concat(h, ":").concat(_pad(d.getMinutes()), " ").concat(ap),
    stale: true,
  };
};
/* ============================================================
   Icons
   ============================================================ */
var ChevronIcon = function ChevronIcon(_ref) {
  var open = _ref.open;
  return /*#__PURE__*/ React.createElement(
    "svg",
    {
      width: "13",
      height: "13",
      viewBox: "0 0 13 13",
      fill: "none",
      style: {
        flexShrink: 0,
        transition: "transform .2s",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
      },
    },
    /*#__PURE__*/ React.createElement("path", {
      d: "M2.5 4.5L6.5 8.5L10.5 4.5",
      stroke: C.faint,
      strokeWidth: "1.6",
      strokeLinecap: "round",
      strokeLinejoin: "round",
    }),
  );
};
var BackArrow = function BackArrow() {
  return /*#__PURE__*/ React.createElement(
    "svg",
    {
      width: "20",
      height: "20",
      viewBox: "0 0 24 24",
      fill: "none",
    },
    /*#__PURE__*/ React.createElement("path", {
      d: "M15 6L9 12L15 18",
      stroke: C.dark,
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
    }),
  );
};
var AvatarSilhouette = function AvatarSilhouette(props) {
  var name = props && props.name;
  if (name) {
    var initials = name
      .trim()
      .split(/\s+/)
      .map(function (w) {
        return w.charAt(0);
      })
      .join("")
      .slice(0, 2)
      .toUpperCase();
    return /*#__PURE__*/ React.createElement(
      "span",
      {
        style: {
          fontSize: "var(--type-body)",
          fontWeight: 700,
          color: "#4A6270",
          letterSpacing: "0.01em",
        },
      },
      initials,
    );
  }
  return /*#__PURE__*/ React.createElement(
    "svg",
    {
      width: "28",
      height: "28",
      viewBox: "0 0 24 24",
      fill: "none",
    },
    /*#__PURE__*/ React.createElement("path", {
      d: "M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
      fill: "#6A8090",
    }),
    /*#__PURE__*/ React.createElement("path", {
      d: "M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8",
      stroke: "#6A8090",
      strokeWidth: "1.5",
      strokeLinecap: "round",
    }),
  );
};
var PencilIcon = function PencilIcon(_refPen) {
  var size = (_refPen || {}).size || 13;
  return /*#__PURE__*/ React.createElement(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 16 16",
      fill: "none",
    },
    /*#__PURE__*/ React.createElement("path", {
      d: "M11 2L14 5L5.5 13.5L2 14L2.5 10.5L11 2Z",
      stroke: C.faint,
      strokeWidth: "1.3",
      strokeLinecap: "round",
      strokeLinejoin: "round",
    }),
  );
};
var CloseIcon = function CloseIcon(_ref2) {
  var color = _ref2.color;
  var size = _ref2.size || 18;
  return /*#__PURE__*/ React.createElement(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 18 18",
      fill: "none",
    },
    /*#__PURE__*/ React.createElement("path", {
      d: "M4 4L14 14M14 4L4 14",
      stroke: color || C.body,
      strokeWidth: "1.8",
      strokeLinecap: "round",
    }),
  );
};
var TrashIcon = function TrashIcon(_ref2a) {
  var _t = _ref2a || {};
  var color = _t.color;
  var size = _t.size || 13;
  return /*#__PURE__*/ React.createElement(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 16 16",
      fill: "none",
    },
    /*#__PURE__*/ React.createElement("path", {
      d: "M2.5 4H13.5M6 4V2.6C6 2.27 6.27 2 6.6 2H9.4C9.73 2 10 2.27 10 2.6V4M6.5 7.2V11.2M9.5 7.2V11.2M3.5 4L4.1 13C4.14 13.66 4.68 14.17 5.34 14.17H10.66C11.32 14.17 11.86 13.66 11.9 13L12.5 4",
      stroke: color || C.faint,
      strokeWidth: "1.3",
      strokeLinecap: "round",
      strokeLinejoin: "round",
    }),
  );
};
var GearIcon = function GearIcon(_refGear) {
  var _g = _refGear || {};
  var size = _g.size || 22;
  var color = _g.color || C.faint;
  return /*#__PURE__*/ React.createElement(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
    },
    /*#__PURE__*/ React.createElement("circle", {
      cx: "12",
      cy: "12",
      r: "3",
      stroke: color,
      strokeWidth: "2",
    }),
    /*#__PURE__*/ React.createElement("path", {
      d: "M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z",
      stroke: color,
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
    }),
  );
};
var NavChevron = function NavChevron(_refNav) {
  var dir = _refNav.dir;
  return /*#__PURE__*/ React.createElement(
    "svg",
    {
      width: "22",
      height: "22",
      viewBox: "0 0 24 24",
      fill: "none",
      style: {
        display: "block",
      },
    },
    /*#__PURE__*/ React.createElement("path", {
      d: dir === "left" ? "M15 18l-6-6 6-6" : "M9 18l6-6-6-6",
      stroke: "currentColor",
      strokeWidth: "2.25",
      strokeLinecap: "round",
      strokeLinejoin: "round",
    }),
  );
};
var LockIcon = function LockIcon(_ref3) {
  var color = _ref3.color;
  return /*#__PURE__*/ React.createElement(
    "svg",
    {
      width: "13",
      height: "13",
      viewBox: "0 0 16 16",
      fill: "none",
    },
    /*#__PURE__*/ React.createElement("rect", {
      x: "3",
      y: "7",
      width: "10",
      height: "7",
      rx: "1.5",
      stroke: color || C.faint,
      strokeWidth: "1.5",
    }),
    /*#__PURE__*/ React.createElement("path", {
      d: "M5.5 7V5a2.5 2.5 0 0 1 5 0v2",
      stroke: color || C.faint,
      strokeWidth: "1.5",
      strokeLinecap: "round",
    }),
  );
};
var StatusIcon = function StatusIcon(_ref4) {
  var iconKey = _ref4.iconKey,
    color = _ref4.color;
  var stroke = color || C.body;
  switch (iconKey) {
    case "bed":
      return /*#__PURE__*/ React.createElement(
        "svg",
        {
          width: "22",
          height: "22",
          viewBox: "0 0 24 24",
          fill: "none",
        },
        /*#__PURE__*/ React.createElement("path", {
          d: "M3 17V8M3 17h18M3 17v3M21 17v3M21 17v-4a2 2 0 0 0-2-2H10v6",
          stroke: stroke,
          strokeWidth: "1.6",
          strokeLinecap: "round",
          strokeLinejoin: "round",
        }),
        /*#__PURE__*/ React.createElement("circle", {
          cx: "7",
          cy: "13",
          r: "2",
          stroke: stroke,
          strokeWidth: "1.6",
        }),
      );
    case "couch":
      return /*#__PURE__*/ React.createElement(
        "svg",
        {
          width: "22",
          height: "22",
          viewBox: "0 0 24 24",
          fill: "none",
        },
        /*#__PURE__*/ React.createElement("path", {
          d: "M3 13v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3M3 13c0-1.1.9-2 2-2h14c1.1 0 2 .9 2 2M3 13v-2a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M15 13v-2a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M5 18v2M19 18v2",
          stroke: stroke,
          strokeWidth: "1.6",
          strokeLinecap: "round",
          strokeLinejoin: "round",
        }),
      );
    case "toilet":
      return /*#__PURE__*/ React.createElement(
        "svg",
        {
          width: "22",
          height: "22",
          viewBox: "0 0 24 24",
          fill: "none",
        },
        /*#__PURE__*/ React.createElement("path", {
          d: "M6 4h12v6a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V4zM9 14l-1 6M15 14l1 6",
          stroke: stroke,
          strokeWidth: "1.6",
          strokeLinecap: "round",
          strokeLinejoin: "round",
        }),
      );
    case "person":
    default:
      return /*#__PURE__*/ React.createElement(
        "svg",
        {
          width: "22",
          height: "22",
          viewBox: "0 0 24 24",
          fill: "none",
        },
        /*#__PURE__*/ React.createElement("circle", {
          cx: "12",
          cy: "7",
          r: "3.5",
          stroke: stroke,
          strokeWidth: "1.6",
        }),
        /*#__PURE__*/ React.createElement("path", {
          d: "M5 21c0-3.9 3.1-7 7-7s7 3.1 7 7",
          stroke: stroke,
          strokeWidth: "1.6",
          strokeLinecap: "round",
        }),
      );
  }
};
var AbnormalityIcon = function AbnormalityIcon(_ref5) {
  var iconKey = _ref5.iconKey,
    color = _ref5.color;
  var stroke = color || C.body;
  switch (iconKey) {
    case "moon_walk":
      return /*#__PURE__*/ React.createElement(
        "svg",
        {
          width: "28",
          height: "28",
          viewBox: "0 0 32 32",
          fill: "none",
        },
        /*#__PURE__*/ React.createElement("path", {
          d: "M22 5a6 6 0 1 0 5 9 7 7 0 0 1-5-9z",
          fill: stroke,
          opacity: "0.18",
        }),
        /*#__PURE__*/ React.createElement("path", {
          d: "M22 5a6 6 0 1 0 5 9 7 7 0 0 1-5-9z",
          stroke: stroke,
          strokeWidth: "1.5",
          strokeLinejoin: "round",
        }),
        /*#__PURE__*/ React.createElement("circle", {
          cx: "10",
          cy: "10",
          r: "1.5",
          fill: stroke,
        }),
        /*#__PURE__*/ React.createElement("path", {
          d: "M9 14v6l-3 4M9 17l3 2v5M12 12l4 2-2 4-4-2",
          stroke: stroke,
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round",
        }),
      );
    case "slow":
      return /*#__PURE__*/ React.createElement(
        "svg",
        {
          width: "28",
          height: "28",
          viewBox: "0 0 32 32",
          fill: "none",
        },
        /*#__PURE__*/ React.createElement("circle", {
          cx: "16",
          cy: "16",
          r: "11",
          stroke: stroke,
          strokeWidth: "1.5",
        }),
        /*#__PURE__*/ React.createElement("path", {
          d: "M16 9v7l4 3",
          stroke: stroke,
          strokeWidth: "1.8",
          strokeLinecap: "round",
          strokeLinejoin: "round",
        }),
      );
    case "toilet":
      return /*#__PURE__*/ React.createElement(
        "svg",
        {
          width: "28",
          height: "28",
          viewBox: "0 0 32 32",
          fill: "none",
        },
        /*#__PURE__*/ React.createElement("path", {
          d: "M8 6h16v8a5 5 0 0 1-5 5h-6a5 5 0 0 1-5-5V6zM12 19l-1 7M20 19l1 7",
          stroke: stroke,
          strokeWidth: "1.6",
          strokeLinecap: "round",
          strokeLinejoin: "round",
        }),
      );
    case "check":
    default:
      return /*#__PURE__*/ React.createElement(
        "svg",
        {
          width: "28",
          height: "28",
          viewBox: "0 0 32 32",
          fill: "none",
        },
        /*#__PURE__*/ React.createElement("circle", {
          cx: "16",
          cy: "16",
          r: "11",
          stroke: stroke,
          strokeWidth: "1.6",
        }),
        /*#__PURE__*/ React.createElement("path", {
          d: "M11 16.5L14.5 20L21 12.5",
          stroke: stroke,
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
        }),
      );
  }
};
var HealthIcon = function HealthIcon(_ref6) {
  var iconKey = _ref6.iconKey,
    color = _ref6.color;
  var stroke = color || C.body;
  if (iconKey === "signal_off")
    return /*#__PURE__*/ React.createElement(
      "svg",
      {
        width: "14",
        height: "14",
        viewBox: "0 0 16 16",
        fill: "none",
      },
      /*#__PURE__*/ React.createElement("path", {
        d: "M2 11.5L8 5.5L11 8.5",
        stroke: stroke,
        strokeWidth: "1.6",
        strokeLinecap: "round",
        strokeLinejoin: "round",
      }),
      /*#__PURE__*/ React.createElement("path", {
        d: "M2 2L14 14",
        stroke: stroke,
        strokeWidth: "1.6",
        strokeLinecap: "round",
      }),
    );
  if (iconKey === "angle_warn")
    return /*#__PURE__*/ React.createElement(
      "svg",
      {
        width: "14",
        height: "14",
        viewBox: "0 0 16 16",
        fill: "none",
      },
      /*#__PURE__*/ React.createElement("path", {
        d: "M8 1.5L15 14H1L8 1.5Z",
        stroke: stroke,
        strokeWidth: "1.6",
        strokeLinejoin: "round",
      }),
      /*#__PURE__*/ React.createElement("path", {
        d: "M8 6v4M8 12v.5",
        stroke: stroke,
        strokeWidth: "1.8",
        strokeLinecap: "round",
      }),
    );
  if (iconKey === "unknown")
    return /*#__PURE__*/ React.createElement(
      "svg",
      {
        width: "14",
        height: "14",
        viewBox: "0 0 16 16",
        fill: "none",
      },
      /*#__PURE__*/ React.createElement("circle", {
        cx: "8",
        cy: "8",
        r: "6.5",
        stroke: stroke,
        strokeWidth: "1.6",
      }),
      /*#__PURE__*/ React.createElement("path", {
        d: "M6 6.2a2 2 0 1 1 3.2 1.6c-.6.5-1.2.9-1.2 1.8",
        stroke: stroke,
        strokeWidth: "1.6",
        strokeLinecap: "round",
        strokeLinejoin: "round",
      }),
      /*#__PURE__*/ React.createElement("circle", {
        cx: "8",
        cy: "12",
        r: "0.7",
        fill: stroke,
      }),
    );
  // online_dot
  return /*#__PURE__*/ React.createElement("span", {
    style: {
      display: "inline-block",
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: stroke,
    },
  });
};

/* ============================================================
   Primitives
   ============================================================ */
var Pill = function Pill(_ref7) {
  var label = _ref7.label,
    colorToken = _ref7.colorToken,
    _ref7$size = _ref7.size,
    size = _ref7$size === void 0 ? "md" : _ref7$size;
  var p = tokenColor(colorToken);
  var isSm = size === "sm";
  return /*#__PURE__*/ React.createElement(
    "span",
    {
      style: {
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: p.bg,
        border: "1.5px solid ".concat(p.border),
        borderRadius: isSm ? 999 : 6,
        padding: isSm ? "2px 9px" : "3px 10px",
        fontSize: "var(--type-caption)",
        fontWeight: 700,
        color: p.text,
        whiteSpace: "nowrap",
      },
    },
    label,
  );
};
var ActionButton = function ActionButton(_ref8) {
  var children = _ref8.children,
    onClick = _ref8.onClick,
    _ref8$variant = _ref8.variant,
    variant = _ref8$variant === void 0 ? "primary" : _ref8$variant,
    _ref8$fullWidth = _ref8.fullWidth,
    fullWidth = _ref8$fullWidth === void 0 ? false : _ref8$fullWidth;
  var styles = {
    primary: {
      background: C.blue,
      color: "white",
      border: "none",
    },
    secondary: {
      background: "white",
      color: C.blue,
      border: "1.5px solid ".concat(C.blueBorder),
    },
  };
  var s = styles[variant] || styles.primary;
  return /*#__PURE__*/ React.createElement(
    "button",
    {
      onClick: onClick,
      style: _objectSpread(
        _objectSpread(
          {
            display: fullWidth ? "flex" : "inline-flex",
            width: fullWidth ? "100%" : undefined,
            justifyContent: "center",
            alignItems: "center",
          },
          s,
        ),
        {},
        {
          fontSize: "var(--type-body)",
          fontWeight: 700,
          borderRadius: 10,
          padding: "10px 14px",
          cursor: "pointer",
          fontFamily: "inherit",
        },
      ),
    },
    children,
  );
};
var Card = function Card(_ref9) {
  var children = _ref9.children,
    style = _ref9.style;
  return /*#__PURE__*/ React.createElement(
    "div",
    {
      style: _objectSpread(
        {
          background: "white",
          border: "1.5px solid ".concat(C.border),
          borderRadius: 14,
          padding: "14px 14px",
          marginBottom: 10,
        },
        style,
      ),
    },
    children,
  );
};
var InlineLink = function InlineLink(_ref0) {
  var children = _ref0.children,
    onClick = _ref0.onClick,
    _ref0$color = _ref0.color,
    color = _ref0$color === void 0 ? C.blue : _ref0$color;
  return /*#__PURE__*/ React.createElement(
    "button",
    {
      onClick: onClick,
      style: {
        background: "transparent",
        border: "none",
        padding: 0,
        cursor: "pointer",
        color: color,
        fontSize: "var(--type-caption)",
        fontWeight: 600,
        fontFamily: "inherit",
        textAlign: "left",
      },
    },
    children,
  );
};
var DeviceHealthBadge = function DeviceHealthBadge(_ref1) {
  var healthState = _ref1.healthState,
    logic = _ref1.logic;
  var entry = getDeviceHealth(logic, healthState);
  var p = tokenColor(entry.color);
  return /*#__PURE__*/ React.createElement(
    "span",
    {
      style: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: p.bg,
        border: "1.5px solid ".concat(p.border),
        borderRadius: 999,
        padding: "3px 10px",
        fontSize: "var(--type-caption)",
        fontWeight: 700,
        color: p.text,
      },
    },
    entry.icon !== "online_dot" &&
      /*#__PURE__*/ React.createElement(HealthIcon, {
        iconKey: entry.icon,
        color: p.value,
      }),
    entry.label,
  );
};

/* ============================================================
   Header
   ============================================================ */
var StatusBar = function StatusBar() {
  return /*#__PURE__*/ React.createElement(
    "div",
    {
      style: {
        background: "white",
        padding: "12px 18px 4px",
        display: "flex",
        justifyContent: "space-between",
      },
    },
    /*#__PURE__*/ React.createElement(
      "span",
      {
        style: {
          fontSize: "var(--type-caption)",
          fontWeight: 700,
          color: C.dark,
        },
      },
      "9:41",
    ),
    /*#__PURE__*/ React.createElement(
      "div",
      {
        style: {
          display: "flex",
          gap: 5,
        },
      },
      /*#__PURE__*/ React.createElement(
        "span",
        {
          style: {
            fontSize: "var(--type-caption)",
            color: C.faint,
          },
        },
        "SOS",
      ),
      /*#__PURE__*/ React.createElement(
        "span",
        {
          style: {
            fontSize: "var(--type-caption)",
            color: C.faint,
          },
        },
        "\u25B2",
      ),
      /*#__PURE__*/ React.createElement(
        "span",
        {
          style: {
            fontSize: "var(--type-caption)",
            color: C.faint,
          },
        },
        "\u258A\u258A\u258A",
      ),
    ),
  );
};
var ScreenHeader = function ScreenHeader(_ref10) {
  var title = _ref10.title,
    logic = _ref10.logic,
    healthState = _ref10.healthState,
    onBack = _ref10.onBack,
    _ref10$showBack = _ref10.showBack,
    showBack = _ref10$showBack === void 0 ? true : _ref10$showBack;
  return /*#__PURE__*/ React.createElement(
    "div",
    {
      style: {
        background: "white",
        borderBottom: "1px solid ".concat(C.borderLight),
        padding: "6px 14px 12px",
        display: "flex",
        alignItems: "center",
        gap: 10,
      },
    },
    showBack &&
      /*#__PURE__*/ React.createElement(
        "button",
        {
          onClick: onBack,
          "aria-label": localizeActivityText("Back", getCurrentLocale()),
          style: {
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 4,
            display: "flex",
            alignItems: "center",
          },
        },
        /*#__PURE__*/ React.createElement(BackArrow, null),
      ),
    /*#__PURE__*/ React.createElement(
      "div",
      {
        style: {
          flex: 1,
          fontSize: "var(--type-body)",
          fontWeight: 700,
          color: C.dark,
        },
      },
      title,
    ),
    healthState !== null &&
      /*#__PURE__*/ React.createElement(DeviceHealthBadge, {
        healthState: healthState,
        logic: logic,
      }),
  );
};

/* ============================================================
   Resident identity
   ============================================================ */
var ResidentIdentity = function ResidentIdentity(_ref11) {
  var resident = _ref11.resident,
    sensor = _ref11.sensor,
    logic = _ref11.logic,
    onSensorDetails = _ref11.onSensorDetails;
  return /*#__PURE__*/ React.createElement(
    "div",
    {
      style: {
        marginBottom: 12,
      },
    },
    /*#__PURE__*/ React.createElement(
      "div",
      {
        style: {
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 6,
        },
      },
      /*#__PURE__*/ React.createElement(
        "div",
        {
          style: {
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: "#CBD8E0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          },
        },
        /*#__PURE__*/ React.createElement(AvatarSilhouette, {
          name: resident.name,
        }),
      ),
      /*#__PURE__*/ React.createElement(
        "div",
        {
          style: {
            flex: 1,
            minWidth: 0,
          },
        },
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              fontSize: "var(--type-body)",
              fontWeight: 700,
              color: C.dark,
            },
          },
          resident.name,
        ),
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              fontSize: "var(--type-caption)",
              color: C.faint,
              fontWeight: 500,
              marginTop: 2,
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
            },
          },
          /*#__PURE__*/ React.createElement(
            "span",
            null,
            resident.gender,
            " | ",
            resident.age,
            resident.location ? " \xB7 ".concat(resident.location) : "",
          ),
        ),
      ),
      /*#__PURE__*/ React.createElement(
        "button",
        {
          onClick: onSensorDetails,
          "aria-label": t(logic, "roomSensorDetailsLink"),
          style: {
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            alignSelf: "flex-start",
            flexShrink: 0,
            color: C.faint,
          },
        },
        /*#__PURE__*/ React.createElement(GearIcon, {
          size: 22,
        }),
      ),
    ),
  );
};

/* ============================================================
   FallIncidentCard
   Two zones: LLM summary + incident rows (one per grouped incident)
   Labels/colors from alertStates in logic. No raw event vocab.
   6-week window, most recent first.
   ============================================================ */
var FallIncidentCard = function FallIncidentCard(_ref12) {
  var fallIncidents = _ref12.fallIncidents,
    fallSummaryText = _ref12.fallSummaryText,
    logic = _ref12.logic;
  var incidents = fallIncidents || [];
  var noFalls = incidents.length === 0;
  var copy = logic.fallIncidentCopy || {};
  var summaryDisplay =
    fallSummaryText ||
    (noFalls
      ? copy.noFallsRecorded || "No falls recorded in the last 6 weeks."
      : null);
  var formatDuration = function formatDuration(startIso, endIso) {
    if (!startIso || !endIso) return null;
    var mins = Math.round((new Date(endIso) - new Date(startIso)) / 60000);
    return mins > 0 ? mins : null;
  };
  return /*#__PURE__*/ React.createElement(
    Card,
    null,
    /*#__PURE__*/ React.createElement(
      "div",
      {
        style: {
          fontSize: "var(--type-caption)",
          fontWeight: 700,
          color: C.dark,
          marginBottom: 10,
        },
      },
      copy.fallSectionHeading || "Fall History · Last 6 weeks",
    ),
    /*#__PURE__*/ React.createElement(
      "div",
      {
        style: {
          background: noFalls ? C.greenBg : C.redBg,
          border: "1.5px solid ".concat(noFalls ? C.greenBorder : C.redBorder),
          borderRadius: 10,
          padding: "10px 14px",
          marginBottom: noFalls ? 0 : 14,
        },
      },
      /*#__PURE__*/ React.createElement(
        "span",
        {
          style: {
            fontSize: "var(--type-caption)",
            fontWeight: noFalls ? 700 : 500,
            color: noFalls ? C.green : C.redText,
            lineHeight: 1.5,
          },
        },
        summaryDisplay,
      ),
    ),
    incidents.length > 0 &&
      /*#__PURE__*/ React.createElement(
        React.Fragment,
        null,
        /*#__PURE__*/ React.createElement("div", {
          style: {
            height: 1,
            background: C.borderLight,
            marginBottom: 12,
          },
        }),
        incidents.map(function (inc, i) {
          var isSuspectedOnly =
            inc.severity === "FALL_SUSPECTED" && !inc.detectedAt;
          var peakAt = inc.detectedAt || inc.suspectedAt;
          var severityEntry = getAlertEntry(
            logic,
            isSuspectedOnly ? "FALL_SUSPECTED" : "FALL_DETECTED",
          );
          var severityPalette = tokenColor(severityEntry.color);
          var recoveredEntry = inc.recoveredAt
            ? getAlertEntry(logic, "FALL_RECOVERED")
            : null;
          var recoveredPalette = recoveredEntry
            ? tokenColor(recoveredEntry.color)
            : null;
          var durationMins = inc.recoveredAt
            ? formatDuration(peakAt, inc.recoveredAt)
            : null;
          return /*#__PURE__*/ React.createElement(
            "div",
            {
              key: inc.id || i,
              style: {
                paddingBottom: i < incidents.length - 1 ? 12 : 0,
                marginBottom: i < incidents.length - 1 ? 12 : 0,
                borderBottom:
                  i < incidents.length - 1
                    ? "1px solid ".concat(C.borderLight)
                    : "none",
                opacity: isSuspectedOnly ? 0.7 : 1,
              },
            },
            /*#__PURE__*/ React.createElement(
              "div",
              {
                style: {
                  fontSize: "var(--type-caption)",
                  color: C.faint,
                  fontWeight: 500,
                  marginBottom: 5,
                },
              },
              formatShortDate(peakAt),
              " \xB7 ",
              formatActivityClock(peakAt, getCurrentLocale()),
            ),
            /*#__PURE__*/ React.createElement(
              "div",
              {
                style: {
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                },
              },
              /*#__PURE__*/ React.createElement(
                "span",
                {
                  style: {
                    fontSize: "var(--type-caption)",
                    fontWeight: 700,
                    color: severityPalette.text,
                  },
                },
                severityEntry.label,
              ),
              recoveredPalette &&
                /*#__PURE__*/ React.createElement(
                  React.Fragment,
                  null,
                  /*#__PURE__*/ React.createElement(
                    "span",
                    {
                      style: {
                        color: C.border,
                        fontSize: "var(--type-caption)",
                      },
                    },
                    "\u2192",
                  ),
                  /*#__PURE__*/ React.createElement(
                    "span",
                    {
                      style: {
                        fontSize: "var(--type-caption)",
                        fontWeight: 600,
                        color: recoveredPalette.text,
                      },
                    },
                    recoveredEntry.label,
                    durationMins &&
                      " \xB7 ".concat(
                        localizeActivityText(
                          "".concat(durationMins, " min"),
                          getCurrentLocale(),
                        ),
                      ),
                  ),
                ),
              !inc.recoveredAt &&
                /*#__PURE__*/ React.createElement(
                  "span",
                  {
                    style: {
                      fontSize: "var(--type-caption)",
                      fontWeight: 500,
                      color: C.redText,
                    },
                  },
                  copy.fallUnresolved || "Not yet recovered",
                ),
            ),
          );
        }),
      ),
  );
};

/* ============================================================
   Last Alert card — replaced by FallIncidentCard above.
   Kept as no-op stub so existing prop references compile.
   ============================================================ */
var LastAlertCard = function LastAlertCard() {
  return null;
};
var AlertHistoryList = function AlertHistoryList() {
  return null;
};

/* ============================================================
   AbnormalityCard — replaced by AbnormalityPatternCard below.
   Kept as no-op stub so existing prop references compile.
   ============================================================ */
var AbnormalityCard = function AbnormalityCard() {
  return null;
};

/* ============================================================
   Resident Status card — inline collapsible activity trend
   ============================================================ */
var ResidentStatusCard = function ResidentStatusCard(_ref13) {
  var _logic$currentStatusD, _logic$currentStatusD2;
  var statusCode = _ref13.statusCode,
    lastSeenAt = _ref13.lastSeenAt,
    trend = _ref13.trend,
    activityInsight = _ref13.activityInsight,
    logic = _ref13.logic;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    trendOpen = _useState2[0],
    setTrendOpen = _useState2[1];
  var entry = getStatusEntry(logic, statusCode);
  var palette = tokenColor(entry.color);
  var rel = formatRelativeTime(
    lastSeenAt,
    ((_logic$currentStatusD = logic.currentStatusDisplay) === null ||
    _logic$currentStatusD === void 0
      ? void 0
      : _logic$currentStatusD.staleThresholdMins) || 60,
  );
  var allBuckets =
    (trend === null || trend === void 0 ? void 0 : trend.buckets) || [];
  var WINDOW = 7;
  var STEP = 7;
  var maxOffset = Math.max(0, allBuckets.length - WINDOW);
  var _useState3 = useState(maxOffset),
    _useState4 = _slicedToArray(_useState3, 2),
    offset = _useState4[0],
    setOffset = _useState4[1];
  var windowBuckets = allBuckets.slice(offset, offset + WINDOW);
  var windowTrend = _objectSpread(
    _objectSpread({}, trend),
    {},
    {
      buckets: windowBuckets,
    },
  );
  var canPrev = offset > 0;
  var canNext = offset < maxOffset;
  var fmtShort = function fmtShort(d) {
    return d ? formatShortDate(d) : "";
  };
  var rangeLabel =
    windowBuckets.length > 0
      ? ""
          .concat(fmtShort(windowBuckets[0].start), " \u2013 ")
          .concat(fmtShort(windowBuckets[windowBuckets.length - 1].start))
      : "";
  var NavBtn = function NavBtn(_ref14) {
    var label = _ref14.label,
      onClick = _ref14.onClick,
      disabled = _ref14.disabled;
    return /*#__PURE__*/ React.createElement(
      "button",
      {
        onClick: onClick,
        disabled: disabled,
        style: {
          fontFamily: "inherit",
          fontSize: "var(--type-heading)",
          fontWeight: 700,
          lineHeight: 1,
          background: "transparent",
          border: "none",
          padding: "4px 8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "default" : "pointer",
          color: disabled ? C.border : C.dark,
        },
      },
      label,
    );
  };
  return /*#__PURE__*/ React.createElement(
    Card,
    null,
    /*#__PURE__*/ React.createElement(
      "div",
      {
        style: {
          fontSize: "var(--type-caption)",
          fontWeight: 700,
          color: C.dark,
          marginBottom: 8,
        },
      },
      t(logic, "residentCurrentlyHeading"),
    ),
    /*#__PURE__*/ React.createElement(
      "div",
      {
        style: {
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
        },
      },
      /*#__PURE__*/ React.createElement(
        "span",
        {
          style: {
            fontSize: "var(--type-body)",
            fontWeight: 700,
            color: C.dark,
          },
        },
        entry.label,
      ),
      rel &&
        /*#__PURE__*/ React.createElement(
          "span",
          {
            style: {
              fontSize: "var(--type-caption)",
              fontWeight: 500,
              color: rel.stale ? C.amber : C.faint,
            },
          },
          ((_logic$currentStatusD2 = logic.currentStatusDisplay) === null ||
          _logic$currentStatusD2 === void 0
            ? void 0
            : _logic$currentStatusD2.separator) || "·",
          " ",
          localizeActivityText(rel.text, getCurrentLocale()),
        ),
    ),
    /*#__PURE__*/ React.createElement(
      "button",
      {
        onClick: function onClick() {
          return setTrendOpen(function (o) {
            return !o;
          });
        },
        style: {
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "transparent",
          border: "1.5px solid ".concat(C.border),
          borderRadius: 10,
          padding: "9px 12px",
          cursor: "pointer",
          fontFamily: "inherit",
        },
      },
      /*#__PURE__*/ React.createElement(
        "span",
        {
          style: {
            fontSize: "var(--type-caption)",
            fontWeight: 600,
            color: C.blue,
          },
        },
        trendOpen
          ? localizeActivityText("Hide Activity Trend", getCurrentLocale())
          : t(logic, "viewActivityTrend"),
      ),
      /*#__PURE__*/ React.createElement(ChevronIcon, {
        open: trendOpen,
      }),
    ),
    trendOpen &&
      /*#__PURE__*/ React.createElement(
        "div",
        {
          style: {
            marginTop: 14,
          },
        },
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            },
          },
          /*#__PURE__*/ React.createElement(NavBtn, {
            label: /*#__PURE__*/ React.createElement(NavChevron, {
              dir: "left",
            }),
            onClick: function onClick() {
              return setOffset(function (o) {
                return Math.max(0, o - STEP);
              });
            },
            disabled: !canPrev,
          }),
          /*#__PURE__*/ React.createElement(
            "div",
            {
              style: {
                fontSize: "var(--type-caption)",
                fontWeight: 600,
                color: C.body,
                textAlign: "center",
              },
            },
            rangeLabel,
            /*#__PURE__*/ React.createElement(
              "div",
              {
                style: {
                  fontSize: "var(--type-caption)",
                  color: C.faint,
                  fontWeight: 400,
                  marginTop: 1,
                },
              },
              formatActivityWeekPosition(
                Math.floor(offset / STEP) + 1,
                Math.ceil(allBuckets.length / STEP),
                getCurrentLocale(),
              ),
            ),
          ),
          /*#__PURE__*/ React.createElement(NavBtn, {
            label: /*#__PURE__*/ React.createElement(NavChevron, {
              dir: "right",
            }),
            onClick: function onClick() {
              return setOffset(function (o) {
                return Math.min(maxOffset, o + STEP);
              });
            },
            disabled: !canNext,
          }),
        ),
        /*#__PURE__*/ React.createElement(StackedColumnChart, {
          trend: windowTrend,
          logic: logic,
        }),
        /*#__PURE__*/ React.createElement(ChartLegend, {
          trend: windowTrend,
          logic: logic,
        }),
        activityInsight &&
          /*#__PURE__*/ React.createElement(ActivityInsightCard, {
            activityInsight: activityInsight,
            logic: logic,
          }),
      ),
  );
};

/* ============================================================
   Stacked column chart for Activity Trend
   Visual treatment ported from Mobility_Analysis_V5.html
   ============================================================ */
var StackedColumnChart = function StackedColumnChart(_ref15) {
  var trend = _ref15.trend,
    logic = _ref15.logic;
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    hoverIdx = _useState6[0],
    setHoverIdx = _useState6[1];
  var buckets =
    (trend === null || trend === void 0 ? void 0 : trend.buckets) || [];
  var series = useMemo(
    function () {
      return visibleTrendSeries(logic, buckets);
    },
    [logic, buckets],
  );
  var yMax = logic.activityTrend.yAxisMaxHours || 24;

  // SVG layout (viewBox-driven for responsiveness)
  var VB_W = 360;
  var VB_H = 240;
  var M = {
    top: 14,
    right: 8,
    bottom: 28,
    left: 40,
  };
  var innerW = VB_W - M.left - M.right;
  var innerH = VB_H - M.top - M.bottom;
  var N = buckets.length || 1;
  var slot = innerW / N;
  var barW = Math.min(slot * 0.55, 28);
  var yTicks = [0, 12, 24];
  var yLabels = [0, 6, 12, 18, 24];
  if (buckets.length === 0) {
    return /*#__PURE__*/ React.createElement(
      "div",
      {
        style: {
          padding: 20,
          textAlign: "center",
          color: C.faint,
          fontSize: "var(--type-caption)",
        },
      },
      localizeActivityText("No trend data available.", getCurrentLocale()),
    );
  }
  return /*#__PURE__*/ React.createElement(
    "div",
    {
      style: {
        position: "relative",
        width: "100%",
      },
    },
    /*#__PURE__*/ React.createElement(
      "svg",
      {
        viewBox: "0 0 ".concat(VB_W, " ").concat(VB_H),
        style: {
          width: "100%",
          height: "auto",
          display: "block",
        },
      },
      yTicks.map(function (y) {
        var yPos = M.top + innerH - (y / yMax) * innerH;
        return /*#__PURE__*/ React.createElement("line", {
          key: y,
          x1: M.left,
          y1: yPos,
          x2: M.left + innerW,
          y2: yPos,
          stroke: C.gridline,
          strokeWidth: "1",
          strokeDasharray: y === 0 ? "" : "2 3",
        });
      }),
      yLabels.map(function (y) {
        var yPos = M.top + innerH - (y / yMax) * innerH;
        return /*#__PURE__*/ React.createElement(
          "text",
          {
            key: y,
            x: M.left - 6,
            y: yPos + 4,
            textAnchor: "end",
            fontSize: "var(--type-caption)",
            fontWeight: "600",
            fill: C.faint,
            fontFamily: "inherit",
            style: {
              fontVariantNumeric: "tabular-nums",
            },
          },
          y + "h",
        );
      }),
      buckets.map(function (b, i) {
        var cx = M.left + slot * (i + 0.5);
        var cumulative = 0;
        return /*#__PURE__*/ React.createElement(
          "g",
          {
            key: b.start || i,
            onMouseEnter: function onMouseEnter() {
              return setHoverIdx(i);
            },
            onMouseLeave: function onMouseLeave() {
              return setHoverIdx(null);
            },
            style: {
              cursor: "pointer",
            },
          },
          /*#__PURE__*/ React.createElement("rect", {
            x: cx - slot / 2,
            y: M.top,
            width: slot,
            height: innerH,
            fill: "transparent",
          }),
          series.map(function (s) {
            var v = typeof b[s.id] === "number" ? b[s.id] : 0;
            if (v <= 0) return null;
            var h = (v / yMax) * innerH;
            var y = M.top + innerH - ((cumulative + v) / yMax) * innerH;
            cumulative += v;
            var palette = tokenColor(s.color);
            return /*#__PURE__*/ React.createElement("rect", {
              key: s.id,
              x: cx - barW / 2,
              y: y,
              width: barW,
              height: h,
              fill: palette.value,
              opacity: hoverIdx === null || hoverIdx === i ? 1 : 0.45,
              rx: "1.5",
            });
          }),
          (function () {
            var showLabel =
              i === 0 || i === N - 1 || i === Math.floor((N - 1) / 2);
            return showLabel
              ? /*#__PURE__*/ React.createElement(
                  "text",
                  {
                    x: cx,
                    y: M.top + innerH + 16,
                    textAnchor: "middle",
                    fontSize: "var(--type-caption)",
                    fill: C.body,
                    fontFamily: "inherit",
                    fontWeight: hoverIdx === i ? 700 : 600,
                  },
                  formatShortDate(b.start),
                )
              : null;
          })(),
        );
      }),
    ),
    hoverIdx !== null &&
      buckets[hoverIdx] &&
      /*#__PURE__*/ React.createElement(
        "div",
        {
          style: {
            position: "absolute",
            left: "".concat(
              ((M.left + slot * (hoverIdx + 0.5)) / VB_W) * 100,
              "%",
            ),
            top: 0,
            transform: "translate(-50%, -8px)",
            background: "#fff",
            border: "1px solid ".concat(C.border),
            borderRadius: 10,
            padding: "8px 11px",
            boxShadow: "0 6px 18px rgba(15,23,42,.18)",
            pointerEvents: "none",
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          },
        },
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              fontSize: "var(--type-caption)",
              color: C.faint,
              fontWeight: 600,
              marginBottom: 4,
            },
          },
          formatShortDate(buckets[hoverIdx].start),
        ),
        series.map(function (s) {
          var v =
            typeof buckets[hoverIdx][s.id] === "number"
              ? buckets[hoverIdx][s.id]
              : null;
          if (v === null) return null;
          var palette = tokenColor(s.color);
          return /*#__PURE__*/ React.createElement(
            "div",
            {
              key: s.id,
              style: {
                fontSize: "var(--type-caption)",
                color: C.dark,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 6,
              },
            },
            /*#__PURE__*/ React.createElement("span", {
              style: {
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: 2,
                background: palette.value,
              },
            }),
            /*#__PURE__*/ React.createElement(
              "span",
              null,
              s.label,
              ": ",
              v.toFixed(1),
              " h",
            ),
          );
        }),
      ),
  );
};
var ChartLegend = function ChartLegend(_ref16) {
  var trend = _ref16.trend,
    logic = _ref16.logic;
  var series = visibleTrendSeries(
    logic,
    trend === null || trend === void 0 ? void 0 : trend.buckets,
  );
  return /*#__PURE__*/ React.createElement(
    "div",
    {
      style: {
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        marginTop: 8,
        marginBottom: 14,
      },
    },
    series.map(function (s) {
      var palette = tokenColor(s.color);
      return /*#__PURE__*/ React.createElement(
        "div",
        {
          key: s.id,
          style: {
            display: "flex",
            alignItems: "center",
            gap: 6,
          },
        },
        /*#__PURE__*/ React.createElement("span", {
          style: {
            display: "inline-block",
            width: 12,
            height: 12,
            borderRadius: 3,
            background: palette.value,
          },
        }),
        /*#__PURE__*/ React.createElement(
          "span",
          {
            style: {
              fontSize: "var(--type-caption)",
              color: C.body,
              fontWeight: 600,
            },
          },
          s.label,
        ),
      );
    }),
  );
};

/* ============================================================
   Activity Trend screen
   ============================================================ */
var RangePickerStub = function RangePickerStub(_ref17) {
  var logic = _ref17.logic,
    fromLabel = _ref17.fromLabel,
    toLabel = _ref17.toLabel;
  return /*#__PURE__*/ React.createElement(
    "div",
    {
      style: {
        display: "flex",
        flexDirection: "column",
        gap: 10,
        marginBottom: 14,
      },
    },
    [
      ["rangeFrom", fromLabel],
      ["rangeTo", toLabel],
    ].map(function (_ref18) {
      var _ref19 = _slicedToArray(_ref18, 2),
        key = _ref19[0],
        val = _ref19[1];
      return /*#__PURE__*/ React.createElement(
        "div",
        {
          key: key,
        },
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              fontSize: "var(--type-caption)",
              fontWeight: 700,
              color: C.faint,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              marginBottom: 4,
            },
          },
          t(logic, key),
        ),
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              background: "white",
              border: "1.5px solid ".concat(C.border),
              borderRadius: 10,
              padding: "10px 12px",
              fontSize: "var(--type-body)",
              color: C.dark,
              fontWeight: 600,
            },
          },
          val,
        ),
      );
    }),
  );
};

/* ============================================================
   AbnormalityPatternCard — single card, two zones:
   1. Summary (LLM-generated summaryText, rendered verbatim)
   2. Signal rows: icon + name + count + dots + trend + definition
   Logic owned by JSON. Frontend never calls LLM.
   ============================================================ */
var SIGNAL_META = {
  GAIT_DECLINE: {
    color: C.teal,
    icon: "slow",
  },
  UNUSUAL_NIGHT_ACTIVITY: {
    color: C.amber,
    icon: "moon_walk",
  },
  PROLONGED_INACTIVITY: {
    color: C.blue,
    icon: "toilet",
  },
};
var AbnormalityPatternCard = function AbnormalityPatternCard(_ref20) {
  var abnormalityPattern = _ref20.abnormalityPattern,
    summaryText = _ref20.summaryText,
    logic = _ref20.logic;
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    openSignal = _useState8[0],
    setOpenSignal = _useState8[1];
  var SIGNALS = [
    "GAIT_DECLINE",
    "UNUSUAL_NIGHT_ACTIVITY",
    "PROLONGED_INACTIVITY",
  ];
  var isAllClear =
    !abnormalityPattern ||
    abnormalityPattern.every(function (p) {
      return p.count === 0;
    }) ||
    (summaryText &&
      summaryText.trim().toLowerCase() === "no abnormalities detected.") ||
    !abnormalityPattern.some(function (p) {
      return p.trend === "WORSENING";
    });
  var displaySummary =
    summaryText || (isAllClear ? "No abnormalities detected." : null);
  return /*#__PURE__*/ React.createElement(
    Card,
    null,
    /*#__PURE__*/ React.createElement(
      "div",
      {
        style: {
          fontSize: "var(--type-caption)",
          fontWeight: 700,
          color: C.dark,
          marginBottom: 10,
        },
      },
      t(logic, "abnormalityHeading"),
    ),
    isAllClear
      ? /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              background: C.greenBg,
              border: "1.5px solid ".concat(C.greenBorder),
              borderRadius: 10,
              padding: "10px 14px",
              marginBottom:
                abnormalityPattern && abnormalityPattern.length ? 14 : 0,
            },
          },
          /*#__PURE__*/ React.createElement(
            "span",
            {
              style: {
                fontSize: "var(--type-caption)",
                fontWeight: 700,
                color: C.green,
              },
            },
            displaySummary,
          ),
        )
      : /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              background: C.amberBg,
              border: "1.5px solid ".concat(C.amberBorder),
              borderRadius: 10,
              padding: "10px 14px",
              marginBottom: 14,
            },
          },
          /*#__PURE__*/ React.createElement(
            "span",
            {
              style: {
                fontSize: "var(--type-caption)",
                fontWeight: 500,
                color: C.amberText,
                lineHeight: 1.5,
              },
            },
            displaySummary,
          ),
        ),
    (abnormalityPattern || []).length > 0 &&
      /*#__PURE__*/ React.createElement(
        React.Fragment,
        null,
        /*#__PURE__*/ React.createElement("div", {
          style: {
            height: 1,
            background: C.borderLight,
            marginBottom: 14,
          },
        }),
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              fontSize: "var(--type-caption)",
              fontWeight: 700,
              color: C.faint,
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              marginBottom: 10,
            },
          },
          localizeActivityText("Last 14 days", getCurrentLocale()),
        ),
        SIGNALS.map(function (signal, si) {
          var data = (abnormalityPattern || []).find(function (p) {
            return p.signal === signal;
          });
          if (!data) return null;
          var copy = getAbnormalityPatternCopy(logic, signal);
          var trendEntry = getTrendDirection(logic, data.trend);
          var trendPalette = tokenColor(trendEntry.color);
          var isOpen = openSignal === signal;
          var meta = SIGNAL_META[signal];
          var headline = copy
            ? interpolate(copy.headlineTemplate, {
                count: data.count,
                window: data.windowDays,
              })
            : "".concat(data.count, " of ").concat(data.windowDays);
          var label = signal.replace(/_/g, " ").replace(/\b\w/g, function (c) {
            return c.toUpperCase();
          });
          return /*#__PURE__*/ React.createElement(
            "div",
            {
              key: signal,
              style: {
                paddingBottom: si < SIGNALS.length - 1 ? 14 : 0,
                marginBottom: si < SIGNALS.length - 1 ? 14 : 0,
                borderBottom:
                  si < SIGNALS.length - 1
                    ? "1px solid ".concat(C.borderLight)
                    : "none",
              },
            },
            /*#__PURE__*/ React.createElement(
              "div",
              {
                style: {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 4,
                },
              },
              /*#__PURE__*/ React.createElement(
                "div",
                {
                  style: {
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  },
                },
                /*#__PURE__*/ React.createElement(AbnormalityIcon, {
                  iconKey: meta.icon,
                  color: meta.color,
                }),
                /*#__PURE__*/ React.createElement(
                  "span",
                  {
                    style: {
                      fontSize: "var(--type-body)",
                      fontWeight: 700,
                      color: C.dark,
                      textTransform: "none",
                    },
                  },
                  localizeActivityText(label, getCurrentLocale()),
                ),
              ),
              /*#__PURE__*/ React.createElement(
                "span",
                {
                  style: {
                    fontSize: "var(--type-caption)",
                    fontWeight: 600,
                    padding: "3px 9px",
                    borderRadius: 999,
                    background: trendPalette.bg,
                    color: trendPalette.text,
                  },
                },
                trendEntry.label,
              ),
            ),
            /*#__PURE__*/ React.createElement(
              "div",
              {
                style: {
                  fontSize: "var(--type-caption)",
                  fontWeight: 500,
                  color: C.dark,
                  marginBottom: 8,
                },
              },
              localizeActivityText(headline, getCurrentLocale()),
            ),
            /*#__PURE__*/ React.createElement(
              "div",
              {
                style: {
                  display: "flex",
                  gap: 4,
                  marginBottom: 6,
                },
              },
              data.days.map(function (flagged, di) {
                return /*#__PURE__*/ React.createElement("div", {
                  key: di,
                  style: {
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: flagged ? meta.color : "white",
                    border: "1.5px solid ".concat(
                      flagged ? meta.color : C.border,
                    ),
                  },
                });
              }),
            ),
            copy &&
              /*#__PURE__*/ React.createElement(
                React.Fragment,
                null,
                /*#__PURE__*/ React.createElement(
                  "button",
                  {
                    onClick: function onClick() {
                      return setOpenSignal(isOpen ? null : signal);
                    },
                    style: {
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      fontSize: "var(--type-caption)",
                      color: C.blue,
                      fontFamily: "inherit",
                    },
                  },
                  localizeActivityText(
                    isOpen ? "< Hide" : "> How it's measured",
                    getCurrentLocale(),
                  ),
                ),
                isOpen &&
                  /*#__PURE__*/ React.createElement(
                    "div",
                    {
                      style: {
                        fontSize: "var(--type-caption)",
                        color: C.dark,
                        lineHeight: 1.55,
                        marginTop: 6,
                        paddingTop: 6,
                        borderTop: "1px solid ".concat(C.borderLight),
                      },
                    },
                    copy.definitionText,
                  ),
              ),
          );
        }),
      ),
  );
};

/* ============================================================
   Activity Insight Card — rendered below trend chart
   Displays backend-computed insightText from logic JSON template.
   Frontend renders verbatim; never computes the insight itself.
   ============================================================ */
var ActivityInsightCard = function ActivityInsightCard(_ref21) {
  var activityInsight = _ref21.activityInsight,
    logic = _ref21.logic;
  var _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    defOpen = _useState0[0],
    setDefOpen = _useState0[1];
  var signal =
    (activityInsight === null || activityInsight === void 0
      ? void 0
      : activityInsight.signal) || "NONE";
  var entry = getInsightCopy(logic, signal);
  var isNone = signal === "NONE";

  // Backend supplies pre-interpolated text via activityInsight.insightText,
  // or we fall back to the template with supplied vars.
  var displayText =
    (activityInsight === null || activityInsight === void 0
      ? void 0
      : activityInsight.insightText) ||
    (entry
      ? interpolate(entry.insightText, {
          delta:
            (activityInsight === null || activityInsight === void 0
              ? void 0
              : activityInsight.delta) || "—",
          days:
            (activityInsight === null || activityInsight === void 0
              ? void 0
              : activityInsight.days) || "—",
        })
      : "");
  var definitionText =
    (entry === null || entry === void 0 ? void 0 : entry.definitionText) || "";
  return /*#__PURE__*/ React.createElement(
    Card,
    null,
    /*#__PURE__*/ React.createElement(
      "div",
      {
        style: {
          background: isNone ? C.greenBg : C.amberBg,
          border: "1.5px solid ".concat(isNone ? C.greenBorder : C.amberBorder),
          borderRadius: 10,
          padding: "10px 14px",
        },
      },
      /*#__PURE__*/ React.createElement(
        "div",
        {
          style: {
            fontSize: "var(--type-caption)",
            color: isNone ? C.green : C.amberText,
            fontWeight: 700,
            lineHeight: 1.5,
          },
        },
        displayText,
      ),
      !isNone &&
        /*#__PURE__*/ React.createElement(
          React.Fragment,
          null,
          /*#__PURE__*/ React.createElement(
            "button",
            {
              onClick: function onClick() {
                return setDefOpen(function (o) {
                  return !o;
                });
              },
              style: {
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                fontSize: "var(--type-caption)",
                color: C.blue,
                fontFamily: "inherit",
                marginTop: 6,
              },
            },
            defOpen ? "< Hide" : "> How it's calculated",
          ),
          defOpen &&
            /*#__PURE__*/ React.createElement(
              "div",
              {
                style: {
                  fontSize: "var(--type-caption)",
                  color: C.faint,
                  lineHeight: 1.55,
                  marginTop: 6,
                  paddingTop: 6,
                  borderTop: "1px solid ".concat(C.borderLight),
                },
              },
              definitionText,
            ),
        ),
    ),
  );
};
var ActivityTrendScreen = function ActivityTrendScreen(_ref22) {
  var resident = _ref22.resident,
    sensor = _ref22.sensor,
    trend = _ref22.trend,
    activityInsight = _ref22.activityInsight,
    logic = _ref22.logic,
    onBack = _ref22.onBack;
  var allBuckets =
    (trend === null || trend === void 0 ? void 0 : trend.buckets) || [];
  var WINDOW = 7;
  var STEP = 7;
  var maxOffset = Math.max(0, allBuckets.length - WINDOW);
  var _useState1 = useState(maxOffset),
    _useState10 = _slicedToArray(_useState1, 2),
    offset = _useState10[0],
    setOffset = _useState10[1]; // start at most recent 14 days

  var windowBuckets = allBuckets.slice(offset, offset + WINDOW);
  var windowTrend = _objectSpread(
    _objectSpread({}, trend),
    {},
    {
      buckets: windowBuckets,
    },
  );
  var fmtLong = function fmtLong(d) {
    return d ? formatShortDate(d) : "";
  };
  var rangeLabel =
    windowBuckets.length > 0
      ? ""
          .concat(fmtLong(windowBuckets[0].start), " \u2013 ")
          .concat(fmtLong(windowBuckets[windowBuckets.length - 1].start))
      : "";
  var canPrev = offset > 0;
  var canNext = offset < maxOffset;
  var NavBtn = function NavBtn(_ref23) {
    var label = _ref23.label,
      onClick = _ref23.onClick,
      disabled = _ref23.disabled;
    return /*#__PURE__*/ React.createElement(
      "button",
      {
        onClick: onClick,
        disabled: disabled,
        style: {
          fontFamily: "inherit",
          fontSize: "var(--type-heading)",
          fontWeight: 700,
          lineHeight: 1,
          background: "transparent",
          border: "none",
          padding: "4px 8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "default" : "pointer",
          color: disabled ? C.border : C.dark,
        },
      },
      label,
    );
  };
  return /*#__PURE__*/ React.createElement(
    React.Fragment,
    null,
    /*#__PURE__*/ React.createElement(ScreenHeader, {
      title: t(logic, "activityTrendTitle"),
      logic: logic,
      healthState: null,
      onBack: onBack,
    }),
    /*#__PURE__*/ React.createElement(
      "div",
      {
        style: {
          padding: 14,
          background: C.bg,
        },
      },
      /*#__PURE__*/ React.createElement(
        "div",
        {
          style: {
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 14,
          },
        },
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "#CBD8E0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            },
          },
          /*#__PURE__*/ React.createElement(AvatarSilhouette, {
            name: resident.name,
          }),
        ),
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              flex: 1,
              minWidth: 0,
            },
          },
          /*#__PURE__*/ React.createElement(
            "div",
            {
              style: {
                fontSize: "var(--type-body)",
                fontWeight: 700,
                color: C.dark,
              },
            },
            resident.name,
          ),
          /*#__PURE__*/ React.createElement(
            "div",
            {
              style: {
                fontSize: "var(--type-caption)",
                color: C.faint,
                fontWeight: 500,
              },
            },
            resident.gender,
            " | ",
            resident.age,
          ),
        ),
      ),
      /*#__PURE__*/ React.createElement(
        Card,
        {
          style: {
            padding: "14px 10px 10px",
          },
        },
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 10,
              paddingLeft: 4,
              paddingRight: 4,
            },
          },
          /*#__PURE__*/ React.createElement(NavBtn, {
            label: /*#__PURE__*/ React.createElement(NavChevron, {
              dir: "left",
            }),
            onClick: function onClick() {
              return setOffset(function (o) {
                return Math.max(0, o - STEP);
              });
            },
            disabled: !canPrev,
          }),
          /*#__PURE__*/ React.createElement(
            "div",
            {
              style: {
                fontSize: "var(--type-caption)",
                fontWeight: 600,
                color: C.body,
                textAlign: "center",
              },
            },
            rangeLabel,
            /*#__PURE__*/ React.createElement(
              "div",
              {
                style: {
                  fontSize: "var(--type-caption)",
                  color: C.faint,
                  fontWeight: 400,
                  marginTop: 1,
                },
              },
              formatActivityWeekPosition(
                Math.floor(offset / STEP) + 1,
                Math.ceil(allBuckets.length / STEP),
                getCurrentLocale(),
              ),
            ),
          ),
          /*#__PURE__*/ React.createElement(NavBtn, {
            label: /*#__PURE__*/ React.createElement(NavChevron, {
              dir: "right",
            }),
            onClick: function onClick() {
              return setOffset(function (o) {
                return Math.min(maxOffset, o + STEP);
              });
            },
            disabled: !canNext,
          }),
        ),
        /*#__PURE__*/ React.createElement(StackedColumnChart, {
          trend: windowTrend,
          logic: logic,
        }),
      ),
      /*#__PURE__*/ React.createElement(ActivityInsightCard, {
        activityInsight: activityInsight,
        logic: logic,
      }),
      /*#__PURE__*/ React.createElement(
        Card,
        null,
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              fontSize: "var(--type-caption)",
              fontWeight: 700,
              color: C.faint,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              marginBottom: 6,
            },
          },
          t(logic, "legend"),
        ),
        /*#__PURE__*/ React.createElement(ChartLegend, {
          trend: windowTrend,
          logic: logic,
        }),
      ),
    ),
  );
};

/* ============================================================
   Sensor Details — slide-up modal (Stage 2)
   ----------------------------------------------------------------
   Renders within the 390px phone shell (parent provides position:
   relative + overflow: hidden). Consumes sensor.details from the
   normalized backend contract:
     { health, lastCheckAt,
       firmware:{ version, isLatest },
       zones:[{ type, label }],
       scanArea:{ left, right, height },
       fallThresholdSeconds,
       errors:[{ code?, label? }] }
   All labels/copy/colors come from logic JSON — no product rules
   are hard-coded here. No backend calls; no MQTT; no credentials.
   ============================================================ */
var SensorDetailRow = function SensorDetailRow(_ref24) {
  var label = _ref24.label,
    children = _ref24.children;
  return /*#__PURE__*/ React.createElement(
    "div",
    {
      style: {
        background: "white",
        border: "1.5px solid ".concat(C.border),
        borderRadius: 12,
        padding: "11px 13px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      },
    },
    label
      ? /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              fontSize: "var(--type-caption)",
              fontWeight: 700,
              color: C.faint,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            },
          },
          label,
        )
      : null,
    /*#__PURE__*/ React.createElement("div", null, children),
  );
};
var SensorDetailsModal = function SensorDetailsModal(_ref25) {
  var sensor = _ref25.sensor,
    logic = _ref25.logic,
    onClose = _ref25.onClose,
    onEdit = _ref25.onEdit,
    onDelete = _ref25.onDelete;
  var details = (sensor && sensor.details) || {};
  // Prefer details.health (Stage 2 contract); fall back to top-level sensor.health.
  var health = details.health || (sensor && sensor.health);
  var lastCheckAt = details.lastCheckAt;
  var wornStatus = details.worn_status || null;
  var batteryStatus = details.battery_status || null;
  var batteryPercent =
    typeof details.battery_percent === "number"
      ? details.battery_percent
      : null;
  var gatewayOnline =
    typeof details.gateway_online === "boolean" ? details.gateway_online : null;
  var dataQuality = details.data_quality || null;
  var errors = Array.isArray(details.errors) ? details.errors : [];
  var wornText =
    wornStatus === "worn"
      ? t(logic, "firmwareLatestBadge")
      : wornStatus === "not_worn"
        ? t(logic, "firmwareUpdateAvailableBadge")
        : t(logic, "firmwareVersionUnknown");
  var wornColor =
    wornStatus === "worn"
      ? "green"
      : wornStatus === "not_worn"
        ? "amber"
        : "gray";
  var batteryText =
    batteryStatus === "ok"
      ? batteryPercent !== null
        ? batteryPercent + "%"
        : "OK"
      : batteryStatus === "low"
        ? batteryPercent !== null
          ? batteryPercent + "% — Low"
          : "Low"
        : t(logic, "fallDelayThresholdUnknown");
  var gatewayText =
    gatewayOnline === true
      ? t(logic, "safetyPerimeterTemplate")
      : gatewayOnline === false
        ? t(logic, "safetyPerimeterUnknown")
        : localizeActivityText("Unknown", getCurrentLocale());
  var dataQualityText =
    dataQuality === "good"
      ? localizeActivityText("Good", getCurrentLocale())
      : dataQuality === "poor"
        ? localizeActivityText("Poor", getCurrentLocale())
        : t(logic, "fallDelayThresholdUnknown");
  return /*#__PURE__*/ React.createElement(
    React.Fragment,
    null,
    /*#__PURE__*/ React.createElement(
      "style",
      null,
      "\n        @keyframes amSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }\n        @keyframes amFadeIn  { from { opacity: 0; } to { opacity: 1; } }\n      ",
    ),
    /*#__PURE__*/ React.createElement(
      "div",
      {
        role: "dialog",
        "aria-modal": "true",
        "aria-label": t(logic, "sensorDetailsTitle"),
        style: {
          position: "absolute",
          inset: 0,
          zIndex: 50,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
        },
      },
      /*#__PURE__*/ React.createElement("div", {
        onClick: onClose,
        style: {
          position: "absolute",
          inset: 0,
          background: "rgba(20,30,50,0.5)",
          animation: "amFadeIn .18s ease-out",
        },
      }),
      /*#__PURE__*/ React.createElement(
        "div",
        {
          style: {
            position: "relative",
            background: "#fff",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: "88%",
            display: "flex",
            flexDirection: "column",
            animation: "amSlideUp .2s ease-out",
            boxShadow: "0 -10px 30px rgba(20,30,50,0.18)",
          },
        },
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              display: "flex",
              justifyContent: "center",
              paddingTop: 8,
              paddingBottom: 2,
            },
          },
          /*#__PURE__*/ React.createElement("div", {
            style: {
              width: 38,
              height: 4,
              borderRadius: 2,
              background: C.border,
            },
          }),
        ),
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "6px 14px 10px",
              borderBottom: "1px solid ".concat(C.borderLight),
            },
          },
          /*#__PURE__*/ React.createElement(
            "div",
            {
              style: {
                fontSize: "var(--type-body)",
                fontWeight: 700,
                color: C.dark,
              },
            },
            t(logic, "sensorDetailsTitle"),
          ),
          /*#__PURE__*/ React.createElement(
            "div",
            {
              style: {
                display: "flex",
                alignItems: "center",
                gap: 4,
              },
            },
            /*#__PURE__*/ React.createElement(
              "button",
              {
                onClick: onEdit,
                "aria-label": t(logic, "editSensorMappingLabel"),
                style: {
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 8,
                  display: "flex",
                  alignItems: "center",
                },
              },
              /*#__PURE__*/ React.createElement(PencilIcon, {
                size: 26,
              }),
            ),
            /*#__PURE__*/ React.createElement(
              "button",
              {
                onClick: onDelete,
                "aria-label": t(logic, "deleteSensorMappingLabel"),
                style: {
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 8,
                  display: "flex",
                  alignItems: "center",
                },
              },
              /*#__PURE__*/ React.createElement(TrashIcon, {
                size: 26,
                color: C.faint,
              }),
            ),
            /*#__PURE__*/ React.createElement(
              "button",
              {
                onClick: onClose,
                "aria-label": t(logic, "sensorDetailsClose"),
                style: {
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 8,
                  display: "flex",
                  alignItems: "center",
                },
              },
              /*#__PURE__*/ React.createElement(CloseIcon, {
                size: 26,
                color: C.faint,
              }),
            ),
          ),
        ),
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              scrollBehavior: "smooth",
              overscrollBehavior: "contain",
              touchAction: "pan-y",
              padding: 14,
              background: C.bg,
              display: "flex",
              flexDirection: "column",
              gap: 9,
            },
          },
          (sensor === null || sensor === void 0 ? void 0 : sensor.sn) &&
            /*#__PURE__*/ React.createElement(
              SensorDetailRow,
              {
                label: localizeActivityText("Device ID", getCurrentLocale()),
              },
              /*#__PURE__*/ React.createElement(
                "span",
                {
                  style: {
                    fontSize: "var(--type-body)",
                    fontWeight: 700,
                    color: C.dark,
                    fontFamily: "var(--font-family-primary)",
                    letterSpacing: "0.02em",
                  },
                },
                sensor.sn,
              ),
            ),
          /*#__PURE__*/ React.createElement(
            SensorDetailRow,
            {
              label: t(logic, "sensorConnectionHeading"),
            },
            /*#__PURE__*/ React.createElement(DeviceHealthBadge, {
              healthState: health,
              logic: logic,
            }),
          ),
          /*#__PURE__*/ React.createElement(
            SensorDetailRow,
            {
              label: t(logic, "lastSystemCheckHeading"),
            },
            /*#__PURE__*/ React.createElement(
              "span",
              {
                style: {
                  fontSize: "var(--type-body)",
                  fontWeight: 600,
                  color: C.dark,
                  fontVariantNumeric: "tabular-nums",
                },
              },
              lastCheckAt
                ? formatFullStamp(lastCheckAt)
                : t(logic, "lastSystemCheckUnknown"),
            ),
          ),
          /*#__PURE__*/ React.createElement(
            SensorDetailRow,
            {
              label: t(logic, "softwareVersionHeading"),
            },
            /*#__PURE__*/ React.createElement(
              "div",
              {
                style: {
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                },
              },
              /*#__PURE__*/ React.createElement(Pill, {
                label: wornText,
                colorToken: wornColor,
                size: "sm",
              }),
            ),
          ),
          /*#__PURE__*/ React.createElement(
            SensorDetailRow,
            {
              label: t(logic, "monitoredZonesHeading"),
            },
            /*#__PURE__*/ React.createElement(
              "span",
              {
                style: {
                  fontSize: "var(--type-body)",
                  fontWeight: 600,
                  color: C.dark,
                },
              },
              batteryText,
            ),
          ),
          /*#__PURE__*/ React.createElement(
            SensorDetailRow,
            {
              label: t(logic, "safetyPerimeterHeading"),
            },
            /*#__PURE__*/ React.createElement(
              "span",
              {
                style: {
                  fontSize: "var(--type-body)",
                  fontWeight: 600,
                  color: C.dark,
                },
              },
              gatewayText,
            ),
          ),
          /*#__PURE__*/ React.createElement(
            SensorDetailRow,
            {
              label: t(logic, "fallDelayThresholdHeading"),
            },
            /*#__PURE__*/ React.createElement(
              "div",
              {
                style: {
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                },
              },
              /*#__PURE__*/ React.createElement(
                "span",
                {
                  style: {
                    fontSize: "var(--type-body)",
                    fontWeight: 700,
                    color: C.dark,
                  },
                },
                dataQualityText,
              ),
              /*#__PURE__*/ React.createElement(
                "span",
                {
                  style: {
                    fontSize: "var(--type-caption)",
                    color: C.faint,
                    lineHeight: 1.45,
                  },
                },
                t(logic, "fallDelayThresholdHelp"),
              ),
            ),
          ),
          /*#__PURE__*/ React.createElement(
            SensorDetailRow,
            {
              label: t(logic, "hardwareStatusHeading"),
            },
            errors.length === 0
              ? /*#__PURE__*/ React.createElement(
                  "div",
                  {
                    style: {
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: "var(--type-caption)",
                      color: C.green,
                      fontWeight: 600,
                    },
                  },
                  /*#__PURE__*/ React.createElement("span", {
                    style: {
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: C.greenV,
                      flexShrink: 0,
                    },
                  }),
                  t(logic, "hardwareStatusOk"),
                )
              : /*#__PURE__*/ React.createElement(
                  "div",
                  {
                    style: {
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    },
                  },
                  errors.map(function (e, i) {
                    return /*#__PURE__*/ React.createElement(
                      "div",
                      {
                        key: i,
                        style: {
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          background: C.redBg,
                          border: "1.5px solid ".concat(C.redBorder),
                          borderRadius: 8,
                          padding: "7px 10px",
                          fontSize: "var(--type-caption)",
                          fontWeight: 600,
                          color: C.redText,
                        },
                      },
                      /*#__PURE__*/ React.createElement("span", {
                        style: {
                          display: "inline-block",
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: C.red,
                          flexShrink: 0,
                        },
                      }),
                      resolveErrorLabel(logic, e),
                    );
                  }),
                ),
          ),
          /*#__PURE__*/ React.createElement(
            "div",
            {
              style: {
                marginTop: 2,
              },
            },
            /*#__PURE__*/ React.createElement(
              "button",
              {
                disabled: true,
                "aria-disabled": "true",
                title: t(logic, "restartSensorNote"),
                style: {
                  width: "100%",
                  background: "#F4F7FA",
                  color: C.faint,
                  border: "1.5px solid ".concat(C.border),
                  borderRadius: 10,
                  padding: "11px 14px",
                  fontSize: "var(--type-body)",
                  fontWeight: 700,
                  cursor: "not-allowed",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                },
              },
              /*#__PURE__*/ React.createElement(LockIcon, null),
              t(logic, "restartSensorLabel"),
            ),
            /*#__PURE__*/ React.createElement(
              "div",
              {
                style: {
                  marginTop: 6,
                  fontSize: "var(--type-caption)",
                  color: C.faint,
                  textAlign: "center",
                  lineHeight: 1.4,
                },
              },
              t(logic, "restartSensorNote"),
            ),
          ),
        ),
      ),
    ),
  );
};

/* ============================================================
   Edit Sensor Mapping modal — used both from Device Details (edit
   an existing binding) and from the empty "Sensor Setup" state
   (add a new binding). Demo behavior:
     - existing binding: Save updates the displayed Device ID locally.
     - empty state ("add"): Save ignores the typed value and shows the
       bundled demo sensor. TODO(api-integration): real world looks the
       entered Sensor ID up in Mongo and binds that sensor's data.
   ============================================================ */
var EditSensorMappingModal = function EditSensorMappingModal(_ref28) {
  var logic = _ref28.logic,
    initialValue = _ref28.initialValue,
    onCancel = _ref28.onCancel,
    onSave = _ref28.onSave;
  var _useState21 = useState(initialValue || ""),
    _useState22 = _slicedToArray(_useState21, 2),
    value = _useState22[0],
    setValue = _useState22[1];
  return /*#__PURE__*/ React.createElement(
    "div",
    {
      role: "dialog",
      "aria-modal": "true",
      "aria-label": t(logic, "editSensorMappingTitle"),
      style: {
        position: "absolute",
        inset: 0,
        zIndex: 60,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      },
    },
    /*#__PURE__*/ React.createElement("div", {
      onClick: onCancel,
      style: {
        position: "absolute",
        inset: 0,
        background: "rgba(20,30,50,0.5)",
      },
    }),
    /*#__PURE__*/ React.createElement(
      "div",
      {
        style: {
          position: "relative",
          width: "100%",
          maxWidth: 340,
          background: "#fff",
          borderRadius: 20,
          padding: 20,
          boxShadow: "0 20px 50px rgba(20,30,50,0.28)",
        },
      },
      /*#__PURE__*/ React.createElement(
        "div",
        {
          style: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          },
        },
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              fontSize: "var(--type-body)",
              fontWeight: 700,
              color: C.dark,
            },
          },
          t(logic, "editSensorMappingTitle"),
        ),
        /*#__PURE__*/ React.createElement(
          "button",
          {
            onClick: onCancel,
            "aria-label": t(logic, "cancelLabel"),
            style: {
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 4,
              display: "flex",
              alignItems: "center",
            },
          },
          /*#__PURE__*/ React.createElement(CloseIcon, null),
        ),
      ),
      /*#__PURE__*/ React.createElement(
        "div",
        {
          style: {
            fontSize: "var(--type-body)",
            color: C.body,
            lineHeight: 1.45,
            marginBottom: 14,
          },
        },
        t(logic, "editSensorMappingDescription"),
      ),
      /*#__PURE__*/ React.createElement(
        "label",
        {
          style: {
            display: "block",
            fontSize: "var(--type-caption)",
            fontWeight: 600,
            color: C.faint,
            marginBottom: 6,
          },
        },
        t(logic, "sensorIdFieldLabel"),
      ),
      /*#__PURE__*/ React.createElement("input", {
        type: "text",
        value: value,
        onChange: function onChange(e) {
          return setValue(e.target.value);
        },
        autoFocus: true,
        style: {
          width: "100%",
          boxSizing: "border-box",
          fontFamily: "var(--font-family-primary)",
          fontSize: "var(--type-body)",
          color: C.dark,
          border: "1.5px solid ".concat(C.border),
          borderRadius: 10,
          padding: "11px 12px",
          marginBottom: 18,
          outline: "none",
        },
      }),
      /*#__PURE__*/ React.createElement(
        "div",
        {
          style: {
            display: "flex",
            gap: 10,
          },
        },
        /*#__PURE__*/ React.createElement(
          "button",
          {
            onClick: onCancel,
            style: {
              flex: 1,
              background: "#EEF2F6",
              color: C.body,
              border: "none",
              borderRadius: 999,
              padding: "12px 14px",
              fontSize: "var(--type-body)",
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
            },
          },
          t(logic, "cancelLabel"),
        ),
        /*#__PURE__*/ React.createElement(
          "button",
          {
            onClick: function onClick() {
              return onSave(value);
            },
            style: {
              flex: 1,
              background: C.blue,
              color: "#fff",
              border: "none",
              borderRadius: 999,
              padding: "12px 14px",
              fontSize: "var(--type-body)",
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
            },
          },
          t(logic, "saveLabel"),
        ),
      ),
    ),
  );
};

/* ============================================================
   Confirm delete modal — styled to match Edit Sensor Mapping.
   ============================================================ */
var ConfirmDeleteSensorModal = function ConfirmDeleteSensorModal(_ref29) {
  var logic = _ref29.logic,
    onCancel = _ref29.onCancel,
    onConfirm = _ref29.onConfirm;
  return /*#__PURE__*/ React.createElement(
    "div",
    {
      role: "dialog",
      "aria-modal": "true",
      "aria-label": t(logic, "deleteSensorMappingTitle"),
      style: {
        position: "absolute",
        inset: 0,
        zIndex: 60,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      },
    },
    /*#__PURE__*/ React.createElement("div", {
      onClick: onCancel,
      style: {
        position: "absolute",
        inset: 0,
        background: "rgba(20,30,50,0.5)",
      },
    }),
    /*#__PURE__*/ React.createElement(
      "div",
      {
        style: {
          position: "relative",
          width: "100%",
          maxWidth: 340,
          background: "#fff",
          borderRadius: 20,
          padding: 20,
          boxShadow: "0 20px 50px rgba(20,30,50,0.28)",
        },
      },
      /*#__PURE__*/ React.createElement(
        "div",
        {
          style: {
            fontSize: "var(--type-body)",
            fontWeight: 700,
            color: C.dark,
            marginBottom: 8,
          },
        },
        t(logic, "deleteSensorMappingTitle"),
      ),
      /*#__PURE__*/ React.createElement(
        "div",
        {
          style: {
            fontSize: "var(--type-body)",
            color: C.body,
            lineHeight: 1.45,
            marginBottom: 18,
          },
        },
        t(logic, "deleteSensorMappingDescription"),
      ),
      /*#__PURE__*/ React.createElement(
        "div",
        {
          style: {
            display: "flex",
            gap: 10,
          },
        },
        /*#__PURE__*/ React.createElement(
          "button",
          {
            onClick: onCancel,
            style: {
              flex: 1,
              background: "#EEF2F6",
              color: C.body,
              border: "none",
              borderRadius: 999,
              padding: "12px 14px",
              fontSize: "var(--type-body)",
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
            },
          },
          t(logic, "cancelLabel"),
        ),
        /*#__PURE__*/ React.createElement(
          "button",
          {
            onClick: onConfirm,
            style: {
              flex: 1,
              background: C.red,
              color: "#fff",
              border: "none",
              borderRadius: 999,
              padding: "12px 14px",
              fontSize: "var(--type-body)",
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
            },
          },
          t(logic, "deleteLabel"),
        ),
      ),
    ),
  );
};

/* ============================================================
   Sensor Setup — empty state shown when no sensor is bound.
   ============================================================ */
var SensorSetupEmptyState = function SensorSetupEmptyState(_ref30) {
  var logic = _ref30.logic,
    onBack = _ref30.onBack,
    onAddSensor = _ref30.onAddSensor;
  return /*#__PURE__*/ React.createElement(
    React.Fragment,
    null,
    /*#__PURE__*/ React.createElement(ScreenHeader, {
      title: t(logic, "screenTitle"),
      logic: logic,
      healthState: null,
      onBack: onBack,
    }),
    /*#__PURE__*/ React.createElement(
      "div",
      {
        style: {
          padding: 16,
          background: C.bg,
        },
      },
      /*#__PURE__*/ React.createElement(
        "div",
        {
          style: {
            background: "#fff",
            borderRadius: 16,
            padding: "28px 20px",
            textAlign: "center",
            boxShadow: "0 1px 3px rgba(20,30,50,0.08)",
          },
        },
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              fontSize: "var(--type-heading)",
              fontWeight: 800,
              color: C.dark,
              marginBottom: 14,
            },
          },
          t(logic, "sensorSetupTitle"),
        ),
        /*#__PURE__*/ React.createElement(
          "div",
          {
            style: {
              fontSize: "var(--type-body)",
              color: C.faint,
              lineHeight: 1.5,
              marginBottom: 22,
            },
          },
          t(logic, "sensorSetupDescription"),
        ),
        /*#__PURE__*/ React.createElement(
          "button",
          {
            onClick: onAddSensor,
            style: {
              width: "100%",
              background: C.blue,
              color: "#fff",
              border: "none",
              borderRadius: 999,
              padding: "13px 14px",
              fontSize: "var(--type-body)",
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
            },
          },
          t(logic, "addSensorLabel"),
        ),
      ),
    ),
  );
};

var ActivityMonitor = function ActivityMonitor(_ref27) {
  var _effectiveData$sensor, _effectiveData$sensor2;
  var residentId = _ref27.residentId,
    activityMonitorData = _ref27.activityMonitorData,
    activityMonitorLogic = _ref27.activityMonitorLogic,
    onBack = _ref27.onBack,
    onSensorMappingChange = _ref27.onSensorMappingChange;
  // TODO(api-integration): When wiring goes live, the parent integration layer
  // will pass `activityMonitorData` and `activityMonitorLogic` down. This
  // component intentionally performs no fetching, no SSE, no WebSocket, no
  // MQTT — see file header for the full integration contract.
  var logic = activityMonitorLogic || LOGIC_DATA;

  // Production: data is always supplied by the integration layer
  // (fetched by subject id, or injected via the `data` prop).
  var effectiveData = activityMonitorData || { sensor: null };
  var _useState13 = useState("main"),
    _useState14 = _slicedToArray(_useState13, 2),
    view = _useState14[0],
    setView = _useState14[1]; // 'main' only — trend is now inline in ResidentStatusCard
  var _useState15 = useState(false),
    _useState16 = _slicedToArray(_useState15, 2),
    historyOpen = _useState16[0],
    setHistoryOpen = _useState16[1];
  var _useState17 = useState(false),
    _useState18 = _slicedToArray(_useState17, 2),
    defOpen = _useState18[0],
    setDefOpen = _useState18[1];
  var _useState19 = useState(false),
    _useState20 = _slicedToArray(_useState19, 2),
    sensorDetailsOpen = _useState20[0],
    setSensorDetailsOpen = _useState20[1];
  var onSensorDetails = function onSensorDetails() {
    return setSensorDetailsOpen(true);
  };

  // --- Sensor mapping: bound/unbound state + demo-local SN override ---
  // TODO(api-integration): in production, binding/unbinding writes
  // resident.sensor_id and the monitor reads the bound sensor's data from
  // Mongo. Here the demo just toggles between the bundled sensor payload
  // and the empty "Sensor Setup" state, and cosmetically overrides the
  // displayed Device ID — no real lookup happens.
  var _useState21 = useState(
      !!(activityMonitorData && activityMonitorData.sensor),
    ),
    _useState22 = _slicedToArray(_useState21, 2),
    sensorBound = _useState22[0],
    setSensorBound = _useState22[1];
  var _useState23 = useState(null),
    _useState24 = _slicedToArray(_useState23, 2),
    snOverride = _useState24[0],
    setSnOverride = _useState24[1];
  var _useState25 = useState(null),
    // null | 'existing' | 'add'
    _useState26 = _slicedToArray(_useState25, 2),
    editMappingMode = _useState26[0],
    setEditMappingMode = _useState26[1];
  var _useState27 = useState(false),
    _useState28 = _slicedToArray(_useState27, 2),
    deleteConfirmOpen = _useState28[0],
    setDeleteConfirmOpen = _useState28[1];

  var sensorForDisplay =
    effectiveData.sensor && snOverride
      ? Object.assign({}, effectiveData.sensor, {
          sn: snOverride,
        })
      : effectiveData.sensor;

  var onEditMapping = function onEditMapping() {
    setSensorDetailsOpen(false);
    setEditMappingMode("existing");
  };
  var onDeleteMapping = function onDeleteMapping() {
    setSensorDetailsOpen(false);
    setDeleteConfirmOpen(true);
  };
  var onAddSensor = function onAddSensor() {
    return setEditMappingMode("add");
  };
  var onSaveMapping = function onSaveMapping(value) {
    var action = editMappingMode === "add" ? "attach" : "update";
    Promise.resolve(
      onSensorMappingChange ? onSensorMappingChange(action, value) : true,
    )
      .then(function (ok) {
        if (ok === false) return;
        if (editMappingMode === "existing") {
          setSnOverride(value);
        } else if (editMappingMode === "add") {
          setSensorBound(true);
        }
        setEditMappingMode(null);
      })
      .catch(function () {});
  };
  var onConfirmDelete = function onConfirmDelete() {
    Promise.resolve(
      onSensorMappingChange ? onSensorMappingChange("detach", null) : true,
    )
      .then(function (ok) {
        if (ok === false) return;
        setSensorBound(false);
        setDeleteConfirmOpen(false);
      })
      .catch(function () {});
  };

  // Render inside the SeniorLife app shell while preserving the Pacsana demo UI.
  return /*#__PURE__*/ React.createElement(
    "div",
    {
      style: {
        fontFamily: FONT_STACK,
        width: "100%",
        minHeight: "100%",
        background: C.bg,
        position: "relative",
      },
    },
    view === "main" &&
      (sensorBound
        ? /*#__PURE__*/ React.createElement(
            React.Fragment,
            null,
            /*#__PURE__*/ React.createElement(ScreenHeader, {
              title: t(logic, "screenTitle"),
              logic: logic,
              healthState:
                (_effectiveData$sensor = effectiveData.sensor) === null ||
                _effectiveData$sensor === void 0
                  ? void 0
                  : _effectiveData$sensor.health,
              onBack: onBack,
            }),
            /*#__PURE__*/ React.createElement(
              "div",
              {
                style: {
                  padding: 14,
                  background: C.bg,
                },
              },
              /*#__PURE__*/ React.createElement(ResidentIdentity, {
                resident: effectiveData.resident,
                sensor: sensorForDisplay,
                logic: logic,
                onSensorDetails: onSensorDetails,
              }),
              /*#__PURE__*/ React.createElement(FallIncidentCard, {
                fallIncidents: effectiveData.fallIncidents,
                fallSummaryText: effectiveData.fallSummaryText,
                logic: logic,
              }),
              /*#__PURE__*/ React.createElement(AbnormalityCard, null),
              /*#__PURE__*/ React.createElement(AbnormalityPatternCard, {
                abnormalityPattern: effectiveData.abnormalityPattern,
                summaryText: effectiveData.summaryText,
                logic: logic,
              }),
              /*#__PURE__*/ React.createElement(ResidentStatusCard, {
                statusCode: effectiveData.currentStatus,
                lastSeenAt:
                  (_effectiveData$sensor2 = effectiveData.sensor) === null ||
                  _effectiveData$sensor2 === void 0
                    ? void 0
                    : _effectiveData$sensor2.lastSeenAt,
                trend: effectiveData.trend,
                activityInsight: effectiveData.activityInsight,
                logic: logic,
              }),
            ),
          )
        : /*#__PURE__*/ React.createElement(SensorSetupEmptyState, {
            logic: logic,
            onBack: onBack,
            onAddSensor: onAddSensor,
          })),
    sensorDetailsOpen &&
      /*#__PURE__*/ React.createElement(SensorDetailsModal, {
        sensor: sensorForDisplay,
        logic: logic,
        onClose: function onClose() {
          return setSensorDetailsOpen(false);
        },
        onEdit: onEditMapping,
        onDelete: onDeleteMapping,
      }),
    editMappingMode &&
      /*#__PURE__*/ React.createElement(EditSensorMappingModal, {
        logic: logic,
        initialValue:
          editMappingMode === "existing"
            ? (sensorForDisplay && sensorForDisplay.sn) || ""
            : "",
        onCancel: function onCancel() {
          return setEditMappingMode(null);
        },
        onSave: onSaveMapping,
      }),
    deleteConfirmOpen &&
      /*#__PURE__*/ React.createElement(ConfirmDeleteSensorModal, {
        logic: logic,
        onCancel: function onCancel() {
          return setDeleteConfirmOpen(false);
        },
        onConfirm: onConfirmDelete,
      }),
  );
};

/* ============================================================
   Data adapter — resident bundle -> component data shape
   ============================================================ */

var _buildTrend = function _buildTrend(days) {
  return {
    interval: "day",
    unit: "hours",
    buckets: days
      .filter(function (d) {
        return d.activityTrend.active_hours != null;
      })
      .map(function (d) {
        var t = d.activityTrend;
        return {
          start: d.date,
          active: t.active_hours,
          sedentary: t.sedentary_hours,
        };
      }),
  };
};
var _buildHistory = function _buildHistory(days) {
  var evts = [];
  days.forEach(function (d) {
    (d.alerts || []).forEach(function (a) {
      if (a.resolvedAs)
        evts.push({
          id: a.event_id + "_r",
          state: a.resolvedAs,
          at: a.resolved_at,
          previousState: a.alertState,
        });
      if (a.alertState === "FALL_DETECTED")
        evts.push({
          id: a.event_id,
          state: "FALL_DETECTED",
          at: a.occurred_at,
          previousState: "FALL_SUSPECTED",
        });
      if (a.alertState === "FALL_SUSPECTED")
        evts.push({
          id: a.event_id + "_s",
          state: "FALL_SUSPECTED",
          at: a.occurred_at,
        });
    });
  });
  return evts.sort(function (a, b) {
    return new Date(b.at) - new Date(a.at);
  });
};
var _toActivityData = function _toActivityData(
  residentRef,
  days,
  sn,
  sensorDetails,
  insightOverride,
  abnormalityPattern,
  summaryText,
  fallIncidents,
  fallSummaryText,
) {
  var last = days[days.length - 1];
  var lastAb =
    last.abnormalities.find(function (a) {
      return a !== "NONE";
    }) || null;
  var activeAlert =
    (last.alerts || []).find(function (a) {
      return !a.resolved;
    }) || null;
  return {
    resident: residentRef,
    sensor: {
      sn: sn,
      health: last.deviceHealth,
      lastSeenAt: last.date + "T18:00:00Z",
      details: _objectSpread(
        _objectSpread({}, sensorDetails),
        {},
        {
          health: last.deviceHealth,
          lastCheckAt: last.date + "T17:55:00Z",
        },
      ),
    },
    currentStatus: last.statusSnapshot,
    currentAlert: activeAlert
      ? {
          state: activeAlert.alertState,
          since: activeAlert.occurred_at,
        }
      : null,
    currentAbnormality: lastAb
      ? {
          id: lastAb,
          since: last.date + "T00:00:00Z",
        }
      : null,
    alertHistory: _buildHistory(days),
    trend: _buildTrend(days),
    activityInsight: insightOverride || {
      signal: "NONE",
      insightText: null,
    },
    abnormalityPattern: abnormalityPattern || [],
    summaryText: summaryText || null,
    fallIncidents: fallIncidents || [],
    fallSummaryText: fallSummaryText || null,
  };
};

export function useActivityData(subjectId, options) {
  options = options || {};
  var idType = options.idType || "seniorlife_id";
  var apiBase = options.apiBase || "/api";
  var injected = options.data || null;

  var _st = useState({
    loading: !injected && !!subjectId,
    error: null,
    payload: injected,
  });
  var state = _st[0],
    setState = _st[1];

  useEffect(
    function () {
      if (injected) {
        setState({ loading: false, error: null, payload: injected });
        return;
      }
      if (!subjectId) {
        setState({ loading: false, error: null, payload: null });
        return;
      }

      var cancelled = false;
      setState(function (s) {
        return { loading: true, error: null, payload: s.payload };
      });

      var url =
        apiBase.replace(/\/+$/, "") +
        "/monitoring/subject/" +
        encodeURIComponent(subjectId) +
        "?idType=" +
        encodeURIComponent(idType);

      fetch(url, { headers: { Accept: "application/json" } })
        .then(function (r) {
          if (!r.ok)
            throw new Error("monitoring fetch failed: HTTP " + r.status);
          return r.json();
        })
        .then(function (json) {
          if (!cancelled)
            setState({ loading: false, error: null, payload: json });
        })
        .catch(function (err) {
          if (!cancelled)
            setState({ loading: false, error: err, payload: null });
        });

      return function () {
        cancelled = true;
      };
    },
    [subjectId, idType, apiBase, injected],
  );

  return state;
}

// Map a backend resident bundle -> the shape ActivityMonitor consumes.
// Accepts camelCase or snake_case keys. Returns a null-sensor bundle when
// no sensor is attached, which drives the SensorSetupEmptyState.
function _bundleToActivityData(payload) {
  if (!payload) return null;
  var pick = function (a, b) {
    return payload[a] != null ? payload[a] : payload[b];
  };

  var days = payload.days || [];
  var attached =
    payload.attached != null
      ? !!payload.attached
      : !!(payload.sensor || (days && days.length));

  if (!attached) {
    return { resident: payload.resident || null, sensor: null };
  }

  var sn = (payload.sensor && payload.sensor.sn) || payload.sn || null;
  var amData = _toActivityData(
    payload.resident,
    days,
    sn,
    pick("sensorDetails", "sensor_details") || {},
    pick("activityInsight", "activity_insight") || null,
    pick("abnormalityPattern", "abnormality_pattern") || [],
    pick("summaryText", "summary_text") || null,
    pick("fallIncidents", "fall_incidents") || [],
    pick("fallSummaryText", "fall_summary_text") || null,
  );

  // _toActivityData SYNTHESIZES sensor.lastSeenAt/health/lastCheckAt from the
  // last report day (a demo shortcut). In production these are live values, so
  // overlay whatever the backend actually provided. If the backend omits them,
  // the synthesized values remain (identical to the demo).
  var s = payload.sensor;
  if (amData && amData.sensor && s && typeof s === "object") {
    var live = s.lastSeenAt || s.last_seen_at;
    var health = s.health;
    var lastCheck = s.lastCheckAt || s.last_check_at;
    if (live) amData.sensor.lastSeenAt = live;
    if (health) {
      amData.sensor.health = health;
      if (amData.sensor.details) amData.sensor.details.health = health;
    }
    if (lastCheck && amData.sensor.details)
      amData.sensor.details.lastCheckAt = lastCheck;
  }
  return amData;
}

var _note = function _note(children) {
  return /*#__PURE__*/ React.createElement(
    "div",
    {
      style: {
        fontFamily: FONT_STACK,
        width: "100%",
        maxWidth: 390,
        margin: "0 auto",
        padding: 48,
        textAlign: "center",
        color: C.faint,
      },
    },
    children,
  );
};

/**
 * Production entry point.
 *
 * props:
 *   subjectId        string   unique seniorlife_id or external_id
 *   idType           string   'seniorlife_id' (default) | 'external_id'
 *   apiBase          string   API root, default '/api'
 *   data             object   optional injected payload (skips fetch)
 *   logic            object   optional logic override (default: bundled JSON)
 *   onBack           fn       back navigation
 *   onAttachSensor   fn(action, value)  'attach'|'update'|'detach' -> hit
 *                             backend bind endpoint, then refetch
 *   renderLoading    fn       optional loading renderer
 *   renderError      fn(err)  optional error renderer
 */
export function ActivityMonitorScreen(props) {
  var res = useActivityData(props.subjectId, {
    idType: props.idType,
    apiBase: props.apiBase,
    data: props.data,
  });

  var activityData = useMemo(
    function () {
      return _bundleToActivityData(res.payload);
    },
    [res.payload],
  );

  if (res.loading) {
    return props.renderLoading
      ? props.renderLoading()
      : _note("Loading monitoring data\u2026");
  }
  if (res.error) {
    return props.renderError
      ? props.renderError(res.error)
      : _note("Could not load monitoring data.");
  }

  return /*#__PURE__*/ React.createElement(ActivityMonitor, {
    activityMonitorData: activityData, // null-sensor bundle -> empty state
    activityMonitorLogic: props.logic || LOGIC_DATA,
    onBack: props.onBack,
    onSensorMappingChange: props.onAttachSensor, // parent wires backend bind/unbind + refetch
  });
}

export function ActivityMonitorView(props) {
  return /*#__PURE__*/ React.createElement(ActivityMonitor, {
    activityMonitorData: props.data,
    activityMonitorLogic: props.logic || LOGIC_DATA,
    onBack: props.onBack,
    onSensorMappingChange: props.onSensorMappingChange || props.onEditSensor,
  });
}

export function ActivityTrendView(props) {
  var data = props.data;
  var logic = props.logic || LOGIC_DATA;
  return /*#__PURE__*/ React.createElement(
    "div",
    {
      style: {
        fontFamily: FONT_STACK,
        position: "relative",
        minHeight: "100%",
        background: C.bg,
      },
    },
    /*#__PURE__*/ React.createElement(ActivityTrendScreen, {
      resident: data.resident,
      sensor: data.sensor,
      trend: data.trend,
      activityInsight: data.activityInsight,
      logic: logic,
      onBack: props.onBack,
    }),
  );
}

export { ActivityMonitor, _toActivityData };
export default ActivityMonitorScreen;
